import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api';
import { CreateQMProps, UpdateAsnProps } from '../../../api/postQM';
import {
  confirmAttachmentRequest,
  createQmRecordRequest,
  CreateQmRecordInput,
  getReceiveManifestQmMatchesRequest,
  ReceiveManifestQmMatchKeyInput,
  getUploadUrlRequest,
  ReceiveManifestQmMatchResult,
  subscribeManifestPartialReceiptUpdates,
  subscribeQmLiveUpdates,
  uploadFileToS3
} from '../../../api/qmCrud';
import {
  getManifestPartialReceiptStatusRequest,
  ManifestPartialReceiptLineStatus,
  saveManifestPartialReceiptRequest
} from '../../../api/manifestPartialReceipt';
import { persistReceiveManifestSnapshot } from '../../../api/saveReceiveManifestSnapshot';
import { variousAsn } from '../../../common/constants';
import {
  convertReceiptEventToCoupaReceiptEvent,
  createQMCoupa,
  createReceiptCoupa,
  createSiteReceiptCoupa,
  updateManifestAsnsCoupa
} from '../../../common/helperFunctions/coupa';
import { printQm } from '../../../common/helperFunctions/print';
import { updateAsnStatus } from '../../../common/helperFunctions/sscc';
import { ManifestLegStatus, ManifestPackageStatus, ManifestStatus } from '../../../common/status';
import { ftlPrefix, galaxyNoteThreeDimensions, transportBookingFormPrefix } from '../../../common/strings';
import {
  DarkUpperSection,
  LightInfoButton,
  LoadingSpinner,
  PageSection,
  SearchField,
  SearchPromptCard,
  StyledButton,
  StyledTableHead,
  StyledTableRow
} from '../../../components';
import DisabledPrintAlert from '../../../components/alerts/DisabledPrintAlert';
import { ConfirmationDialog } from '../../../components/modals/ConfirmDialog';
import DirectReceiptModel from '../../../components/modals/DirectReceiptForm';
import LoadingModal from '../../../components/modals/LoadingModal';
import RaiseQMNewForm, {
  QmSiteInboundAsnLine,
  QmSiteInboundManifestContext,
  RaiseQMNewModel
} from '../../../components/modals/RaiseQMNewForm';
import { ReceiptEventPackageProps } from '../../../datastore/ssccs';
import {
  ManifestPackageInvLineItemProps,
  ManifestPackageProps,
  ManifestProps,
  ManifestSSCCProps,
  TripSheetProps
} from '../../../datastore/tripSheets';
import { UserDetailsProps } from '../../../datastore/userDetails';
import { useAlert, useCurrentUser, useDatastore, useIsOnline, useQmFeatureFlags } from '../../../hooks';
import { useDirtyContext } from '../../../hooks/useDirtyProvider';
import { useQmDetailsDialog } from '../../../contexts/QmDetailsDialogContext';
import { useDisabledPrinterContext } from '../../../hooks/useIsDisabledPrinter';
import useObserveTripsheet from '../../../hooks/useObserveTripsheet';
import { AsnLineReceiveQuantityOverrides, getAsnLineReceiveQuantityKey, mapToReceiptedItems } from './detail.mapping';
import { normalizeManifestPackageStatuses } from './detail.manifestSync';
import { applyPartialReceiptQuantitiesToManifest } from './detail.partialOverlay';
import { ReceiveManifestNavigationState, resolveReceiveManifestNavigationContext } from './detail.navigation';
import {
  registerPendingRequest,
  updateRequestStatus,
  queueOfflineRequest
} from '../../../common/helperFunctions/duplicatePreventionUtils';
import { captureError } from '../../../common/sentry';
import { compressImageForUpload } from '../../../common/imageCompression';
import { QMPrintProps } from '../../../../../Common/Types/types';

interface QMDictionary {
  key: string;
  value: CreateQMProps;
}

interface DirectDictionary {
  key: string;
  value: ReceiptEventPackageProps;
}

type ManifestReceiptLineResultInput = {
  asn: string;
  poLineId: string;
  itemId?: string;
  sourcePartId?: string;
  packageId?: string;
  selectedForReceive: boolean;
  requestedQuantity?: number;
  processedQuantity?: number;
  receiveQuantity?: number;
  status: string;
  errorCode?: string;
  errorMessage?: string;
};

type HistoryDialogContext = {
  ssccId: string;
  asn: string;
  poNumber: string;
  packageLabel: string;
  packageType: string;
  stockStatus: string;
  status: string;
  poLineId: string;
  itemId: string;
  sourcePartId: string;
  lineDescription: string;
  packageId: string;
};

const MANIFEST_QM_SSCC_REASON_CODES = [
  'SITE_INBOUND_SSCC_NOT_DELIVERED',
  'SITE_INBOUND_DAMAGED_IN_TRANSIT',
  'SITE_INBOUND_NOT_FIT_FOR_TRANSPORT'
];

const MANIFEST_QM_ASN_REASON_CODES = [
  'SITE_INBOUND_ASN_NOT_DELIVERED',
  'SITE_INBOUND_INCORRECT_GOODS',
  'SITE_INBOUND_INCORRECT_QTY'
];
const RECEIVE_MANIFEST_QM_ASN_LEVEL_KEY = '__ASN__';
const ITEM_STATUS_FILTER_ALL = 'ALL';
const MAX_EXPAND_ALL_SSCC_COUNT = 50;
const MAX_EXPAND_ALL_ASN_GROUP_COUNT = 80;
const ITEM_STATUS_FILTER_OPTIONS = [
  { value: ITEM_STATUS_FILTER_ALL, label: 'All Item Statuses' },
  { value: ManifestPackageStatus.notReceived, label: ManifestPackageStatus.notReceived },
  { value: ManifestPackageStatus.partial, label: ManifestPackageStatus.partial },
  { value: ManifestPackageStatus.received, label: ManifestPackageStatus.received },
  { value: ManifestPackageStatus.qm, label: ManifestPackageStatus.qm },
  { value: ManifestPackageStatus.direct, label: ManifestPackageStatus.direct }
];
const GET_SSCC_DESTINATION_LOCATION_ID = /* GraphQL */ `
  query GetSsccDestinationLocationId($id: ID!) {
    getSSCC(id: $id) {
      destinationLocationId
    }
  }
`;

export default function ReceiveManifestDetails() {
  const { id } = useParams();
  const theme = useTheme();
  const alert = useAlert();
  const store = useDatastore();
  const user = useCurrentUser();
  const { isOnline } = useIsOnline();
  const { qmSiteInboundEnabled: isQmSiteInboundEnabled } = useQmFeatureFlags();
  const { setIsDirty } = useDirtyContext();
  const { openQmDetails } = useQmDetailsDialog();
  const { isDisabled: isAnyPrinterDisabled } = useDisabledPrinterContext();
  const isMobile = useMediaQuery(galaxyNoteThreeDimensions);
  const isPhoneLayout = useMediaQuery('(max-width:900px), (max-height:600px)');
  const isNarrowDesktop = !isPhoneLayout;
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as ReceiveManifestNavigationState | undefined;
  const { tripId, legStatus, legDestination, tripReference } = resolveReceiveManifestNavigationContext(
    navigationState,
    location.search
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialPostgresStatusLoaded, setIsInitialPostgresStatusLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tripData, setTripData] = useState<TripSheetProps>();
  const [manifest, setManifest] = useState<ManifestProps>();
  const manifestRef = useRef<ManifestProps>();
  const [search, setSearch] = useState('');
  const [openRaiseQM, setOpenRaiseQM] = useState(false);
  const [openQmScopeDialog, setOpenQmScopeDialog] = useState(false);
  const [openDirectModel, setopenDirectModel] = useState(false);
  const [selectedASN, setSelectedASN] = useState<string>('');
  const [selectedQmScope, setSelectedQmScope] = useState<'sscc' | 'asn'>('asn');
  const [selectedQmSsccId, setSelectedQmSsccId] = useState<string>('');
  const [selectedQmSiteId, setSelectedQmSiteId] = useState<string | null>(null);
  const [pendingQmOpenKey, setPendingQmOpenKey] = useState<string | null>(null);
  const pendingQmOpenKeyRef = useRef<string | null>(null);
  const [pendingQmScopeSelection, setPendingQmScopeSelection] = useState<{
    asn: string;
    ssccId: string;
  } | null>(null);
  const [qmDictionary, setQMDictionary] = useState<QMDictionary[]>([]);
  const [directDictionary, setDirectDictionary] = useState<DirectDictionary[]>([]);
  const [filteredSsccs, setFilteredSsccs] = useState<ManifestSSCCProps[]>();
  const [itemStatusFilter, setItemStatusFilter] = useState(ITEM_STATUS_FILTER_ALL);
  const [itemStatusFilterCounts, setItemStatusFilterCounts] = useState<Record<string, number>>({
    [ITEM_STATUS_FILTER_ALL]: 0
  });
  const [originalManifest, setOriginalManifest] = useState<ManifestProps>();
  const [openDirtyDialog, setOpenDirtyDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetailsProps | null>(null);
  const [selectedPartialLineKeys, setSelectedPartialLineKeys] = useState<Record<string, boolean>>({});
  const [partialStatusHistoryByKey, setPartialStatusHistoryByKey] = useState<
    Record<string, ManifestPartialReceiptLineStatus[]>
  >({});
  const [qmMatchesByLineKey, setQmMatchesByLineKey] = useState<Record<string, ReceiveManifestQmMatchResult[]>>({});
  const qmMatchesByLineKeyRef = useRef<Record<string, ReceiveManifestQmMatchResult[]>>({});
  const qmMatchRefreshSeqRef = useRef(0);
  const [expandedHistoryRowKey, setExpandedHistoryRowKey] = useState<string | null>(null);
  const [expandedHistoryEntries, setExpandedHistoryEntries] = useState<ManifestPartialReceiptLineStatus[]>([]);
  const [expandedHistoryContext, setExpandedHistoryContext] = useState<HistoryDialogContext | null>(null);
  const [qmPickerAnchorEl, setQmPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [qmPickerMatches, setQmPickerMatches] = useState<ReceiveManifestQmMatchResult[]>([]);
  const [isPartialStatusLoading, setIsPartialStatusLoading] = useState(false);
  const [expandedSsccs, setExpandedSsccs] = useState<Record<string, boolean>>({});
  const [expandedAsnGroups, setExpandedAsnGroups] = useState<Record<string, boolean>>({});
  const tripsheet = useObserveTripsheet(tripId || '');
  const submitRequestIdRef = useRef<string | null>(null);
  const initialPostgresStatusLoadedManifestIdRef = useRef<string | null>(null);
  const userEmail =
    String(user.email || user.cognitoUser?.attributes?.email || '') || String(user.cognitoUser?.username || '');
  const isUiSectionStateHydratedRef = useRef(false);
  const skipNextUiSectionPersistRef = useRef(false);
  const autoExpandedSsccManifestKeyRef = useRef<string | null>(null);
  const ssccIdsSignature = manifest?.ssccs.map((sscc) => sscc.ssccId).join('|') || '';
  const tableActionButtonSx = {
    minHeight: '2em',
    padding: '0.28em 0.68em',
    minWidth: '5.2em',
    fontSize: '0.72rem',
    lineHeight: 1.2,
    fontWeight: 600,
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    textTransform: 'none'
  };
  const quantityInputSx = {
    width: '5.5em',
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      borderRadius: '8px'
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.disabled
    }
  };
  const tableCheckboxSx = {
    p: 0,
    color: theme.palette.grey[500],
    '&.Mui-checked': {
      color: theme.palette.grey[700]
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[700]
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[300]
    }
  };
  const partialSelectColumnSx = {
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    px: 0,
    verticalAlign: 'middle',
    textAlign: 'center',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      mx: 'auto'
    },
    '& .MuiCheckbox-root': {
      mx: 'auto'
    },
    '& .MuiSvgIcon-root': {
      display: 'block',
      mx: 'auto'
    }
  };
  const partialSuccessIconSx = { color: theme.palette.grey[700] };
  const actionColumnSx = {
    width: 104,
    minWidth: 104,
    maxWidth: 104,
    px: 0,
    verticalAlign: 'middle',
    textAlign: 'center',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      mx: 'auto'
    }
  };
  const historyColumnSx = {
    width: 64,
    minWidth: 64,
    maxWidth: 64,
    px: 0,
    verticalAlign: 'middle',
    textAlign: 'center',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      mx: 'auto'
    }
  };
  const asnIconSlotWidth = '1.7em';
  const asnSecondaryTextIndent = `calc(${asnIconSlotWidth} + ${theme.spacing(0.5)})`;
  const expandedHistory = expandedHistoryRowKey ? expandedHistoryEntries : [];
  const latestExpandedHistory = expandedHistory[0] || null;

  function getPrimaryLine(pkg: ManifestPackageProps) {
    return pkg.inventoryLines?.[0] || null;
  }

  function getAsnGroupKey(ssccId: string, asn: string): string {
    return `${ssccId}::${asn}`;
  }

  function isAsnGroupExpandable(pkg: ManifestPackageProps): boolean {
    return !isVarious(pkg) && String(pkg.asn || '').trim() !== '1';
  }

  function getAsnInventoryLines(sscc: ManifestSSCCProps, asn: string): ManifestPackageInvLineItemProps[] {
    const allLines = sscc.packages.filter((pkg) => pkg.asn === asn).flatMap((pkg) => pkg.inventoryLines || []);

    const uniqueByLine = new Map<string, ManifestPackageInvLineItemProps>();
    for (const line of allLines) {
      const key = `${line.orderLineId || ''}::${line.partId || ''}::${line.sourcePartId || ''}`;
      if (!uniqueByLine.has(key)) {
        uniqueByLine.set(key, line);
      }
    }

    return Array.from(uniqueByLine.values());
  }

  function findPackageForAsnLine(
    asn: string | undefined,
    line: ManifestPackageInvLineItemProps
  ): ManifestPackageProps | null {
    if (!asn || !manifest) {
      return null;
    }

    return (
      manifest.ssccs
        .flatMap((sscc) => sscc.packages)
        .find(
          (pkg) =>
            String(pkg.asn || '').trim() === String(asn || '').trim() &&
            (pkg.inventoryLines || []).some(
              (candidate) =>
                String(candidate.orderLineId || '').trim() === String(line.orderLineId || '').trim() &&
                String(candidate.partId || '').trim() === String(line.partId || '').trim() &&
                String(candidate.sourcePartId || '').trim() === String(line.sourcePartId || '').trim()
            )
        ) || null
    );
  }

  function getDefaultAsnLineReceiveQuantity(line: ManifestPackageInvLineItemProps): number {
    return Number(line.quantityReceived || 0);
  }

  function getSuccessfulReceivedQuantityForAsnLine(asn: string, line: ManifestPackageInvLineItemProps): number {
    const normalizedAsn = String(asn || '')
      .trim()
      .toLowerCase();
    const normalizedPoLineId = String(line.orderLineId || '')
      .trim()
      .toLowerCase();
    const normalizedItemId = String(line.partId || '')
      .trim()
      .toLowerCase();

    return (
      successfulReceivedQuantityByAsnLineKey.get(`${normalizedAsn}::${normalizedPoLineId}::${normalizedItemId}`) || 0
    );
  }

  function getRemainingAsnLineReceiveQuantity(asn: string, line: ManifestPackageInvLineItemProps): number {
    return Math.max(0, getDefaultAsnLineReceiveQuantity(line) - getSuccessfulReceivedQuantityForAsnLine(asn, line));
  }

  function getAsnLineReceiveQuantity(asn: string, line: ManifestPackageInvLineItemProps): number {
    return getRemainingAsnLineReceiveQuantity(asn, line);
  }

  function buildCalculatedAsnLineReceiveQuantityOverrides(
    sourceManifest: ManifestProps,
    options: { excludeOpenQmLines?: boolean } = {}
  ): AsnLineReceiveQuantityOverrides {
    return sourceManifest.ssccs.reduce((acc, sscc) => {
      sscc.packages.forEach((pkg) => {
        (pkg.inventoryLines || []).forEach((line) => {
          acc[getAsnLineReceiveQuantityKey(pkg.asn, line, sscc.ssccId)] =
            options.excludeOpenQmLines && getOpenQmMatchForLine(pkg.asn, line, sscc.ssccId)
              ? 0
              : getRemainingAsnLineReceiveQuantity(pkg.asn, line);
        });
      });
      return acc;
    }, {} as AsnLineReceiveQuantityOverrides);
  }

  function getQmMatchLineKey(
    asn: string,
    poLineId?: string | number | null,
    itemId?: string | number | null,
    sscc?: string | number | null
  ): string {
    return `${String(asn || '')
      .trim()
      .toLowerCase()}::${String(poLineId || '')
      .trim()
      .toLowerCase()}::${String(itemId || '')
      .trim()
      .toLowerCase()}::${String(sscc || '')
      .trim()
      .toLowerCase()}`;
  }

  function getQmMatchAsnKey(asn: string, sscc?: string | number | null): string {
    return getQmMatchLineKey(asn, RECEIVE_MANIFEST_QM_ASN_LEVEL_KEY, null, sscc);
  }

  function getQmMatchKeysForLine(
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchKeyInput[] {
    const poLineId = String(line.orderLineId || '').trim();
    if (!asn || !poLineId) {
      return [];
    }

    const itemIds = [line.partId, line.sourcePartId]
      .map((value) => String(value || '').trim())
      .filter((value, index, values) => value.length > 0 && values.indexOf(value) === index);

    return itemIds.length > 0
      ? itemIds.map((itemId) => ({ asn, poLineId, itemId, sscc: String(sscc || '').trim() || null }))
      : [{ asn, poLineId, itemId: null, sscc: String(sscc || '').trim() || null }];
  }

  function getQmMatchKeysForPackages(
    packages: ManifestPackageProps[],
    sscc?: string | number | null
  ): ReceiveManifestQmMatchKeyInput[] {
    const uniqueKeys = new Map<string, ReceiveManifestQmMatchKeyInput>();
    const ssccValue = String(sscc || '').trim() || null;
    packages.forEach((pkg) => {
      const asn = String(pkg.asn || '').trim();
      if (asn) {
        uniqueKeys.set(getQmMatchAsnKey(asn, ssccValue), {
          asn,
          poLineId: RECEIVE_MANIFEST_QM_ASN_LEVEL_KEY,
          itemId: null,
          sscc: ssccValue
        });
      }
      (pkg.inventoryLines || []).forEach((line) => {
        getQmMatchKeysForLine(pkg.asn, line, ssccValue).forEach((key) => {
          uniqueKeys.set(getQmMatchLineKey(key.asn, key.poLineId, key.itemId, key.sscc), key);
        });
      });
    });

    return Array.from(uniqueKeys.values());
  }

  function getQmMatchFromMap(
    matchesByLineKey: Map<string, ReceiveManifestQmMatchResult[]>,
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult | null {
    const candidateMatches: ReceiveManifestQmMatchResult[] = [];
    for (const key of getQmMatchKeysForLine(asn, line, sscc)) {
      candidateMatches.push(
        ...(matchesByLineKey.get(getQmMatchLineKey(key.asn, key.poLineId, key.itemId, key.sscc)) || [])
      );
    }

    candidateMatches.push(
      ...(matchesByLineKey.get(getQmMatchLineKey(asn, line.orderLineId, null, sscc)) || []),
      ...(matchesByLineKey.get(getQmMatchAsnKey(asn, sscc)) || [])
    );
    return (
      candidateMatches.find((match) => match.qmId && isOpenBlockingQmMatch(match)) ||
      candidateMatches.find((match) => {
        const status = String(match.status || '').toUpperCase();
        return match.qmId && (status === 'CREATED' || status === 'PENDING');
      }) ||
      candidateMatches.find((match) => match.qmId) ||
      null
    );
  }

  function getQmMatchesForLine(
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult[] {
    const matchesByQm = new Map<string, ReceiveManifestQmMatchResult>();
    const matchesByLineKey = qmMatchesByLineKeyRef.current;
    if (line?.orderLineId) {
      for (const key of getQmMatchKeysForLine(asn, line, sscc)) {
        const matches = matchesByLineKey[getQmMatchLineKey(key.asn, key.poLineId, key.itemId, key.sscc)] || [];
        matches.forEach((match) => {
          if (match?.qmId) {
            matchesByQm.set(String(match.qmId || match.qmNumber), match);
          }
        });
      }

      const directLineMatches = matchesByLineKey[getQmMatchLineKey(asn, line.orderLineId, null, sscc)] || [];
      directLineMatches.forEach((match) => {
        if (match?.qmId) {
          matchesByQm.set(String(match.qmId || match.qmNumber), match);
        }
      });
    }

    const asnMatches = matchesByLineKey[getQmMatchAsnKey(asn, sscc)] || [];
    asnMatches.forEach((match) => {
      if (match?.qmId) {
        matchesByQm.set(String(match.qmId || match.qmNumber), match);
      }
    });

    return Array.from(matchesByQm.values());
  }

  function getAsnLineQuantityHelpText(asn: string | undefined, line: ManifestPackageInvLineItemProps): string {
    if (!asn) {
      return 'Source quantity';
    }

    return 'Source quantity';
  }

  function isStockPurchaseType(pkg: ManifestPackageProps): boolean {
    const purchaseType = getPrimaryLine(pkg)?.purchaseType;
    return (
      String(purchaseType || '')
        .trim()
        .toLowerCase() === 'stock'
    );
  }

  function getDistinctAsnCountExcludingVarious(sscc: ManifestSSCCProps): number {
    const uniqueAsns = new Set<string>();

    for (const pkg of sscc.packages) {
      const asn = String(pkg.asn || '').trim();
      if (!asn) {
        continue;
      }

      if (asn.toUpperCase() === variousAsn.toUpperCase()) {
        continue;
      }

      uniqueAsns.add(asn.toUpperCase());
    }

    return uniqueAsns.size;
  }

  function formatAsnCountLabel(count: number): string {
    return `${count} ${count === 1 ? 'ASN' : 'ASNs'}`;
  }

  function doesAsnMatchSearch(asn?: string): boolean {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return false;
    }

    return String(asn || '')
      .toLowerCase()
      .includes(normalizedSearch);
  }

  function renderHighlightedAsn(asn?: string) {
    const asnText = String(asn || '');
    const normalizedSearch = search.trim().toLowerCase();

    if (!asnText || !normalizedSearch) {
      return asnText;
    }

    const matchStart = asnText.toLowerCase().indexOf(normalizedSearch);
    if (matchStart < 0) {
      return asnText;
    }

    const matchEnd = matchStart + normalizedSearch.length;
    const beforeMatch = asnText.slice(0, matchStart);
    const matchText = asnText.slice(matchStart, matchEnd);
    const afterMatch = asnText.slice(matchEnd);

    return (
      <>
        {beforeMatch}
        <Box
          component="span"
          sx={{
            px: 0.3,
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 230, 0, 0.35)',
            fontWeight: 700
          }}>
          {matchText}
        </Box>
        {afterMatch}
      </>
    );
  }

  async function resolveDestinationLocationIdForSscc(ssccId: string): Promise<string | null> {
    if (!ssccId) return null;
    try {
      const result = (await API.graphql(
        graphqlOperation(GET_SSCC_DESTINATION_LOCATION_ID, { id: ssccId })
      )) as GraphQLResult<{
        getSSCC?: { destinationLocationId?: string | null } | null;
      }>;
      return result.data?.getSSCC?.destinationLocationId || null;
    } catch (error) {
      captureError(error, 'receiveManifest-resolveDestinationLocationIdForSscc', { extra: { ssccId } });
      return null;
    }
  }

  const manifestDestinationBusinessName = useMemo(() => {
    const runtimeManifestDestination = (manifest as unknown as { destination?: { businessName?: string | null } })
      ?.destination?.businessName;
    const manifestLegDestination = tripData?.legs.find((leg) =>
      leg.manifests.some((currentManifest) => currentManifest.id === manifest?.id)
    )?.destination?.businessName;
    return String(runtimeManifestDestination || manifestLegDestination || legDestination || '').trim();
  }, [legDestination, manifest, tripData?.legs]);

  function getQmOpenKey(scope: 'asn' | 'sscc', ssccId: string, asn = '') {
    return `${scope}:${ssccId}:${asn}`;
  }

  async function openQmForAsn(asn: string, ssccId: string) {
    if (isManifestInteractionLocked) return;

    if (!isQmSiteInboundEnabled) {
      alert('Site Inbound QM is currently disabled.', 'warning');
      return;
    }

    const openKey = getQmOpenKey('asn', ssccId, asn);
    if (pendingQmOpenKeyRef.current) return;
    pendingQmOpenKeyRef.current = openKey;
    setPendingQmOpenKey(openKey);
    try {
      const siteId = await resolveDestinationLocationIdForSscc(ssccId);
      setSelectedASN(asn);
      setSelectedQmScope('asn');
      setSelectedQmSsccId(ssccId);
      setSelectedQmSiteId(siteId);
      setOpenRaiseQM(true);
    } finally {
      pendingQmOpenKeyRef.current = null;
      setPendingQmOpenKey(null);
    }
  }

  async function openQmForSscc(asn: string, ssccId: string) {
    if (isManifestInteractionLocked) return;

    if (!isQmSiteInboundEnabled) {
      alert('Site Inbound QM is currently disabled.', 'warning');
      return;
    }

    const openKey = getQmOpenKey('sscc', ssccId, asn);
    if (pendingQmOpenKeyRef.current) return;
    pendingQmOpenKeyRef.current = openKey;
    setPendingQmOpenKey(openKey);
    try {
      const siteId = await resolveDestinationLocationIdForSscc(ssccId);
      setSelectedASN(asn);
      setSelectedQmScope('sscc');
      setSelectedQmSsccId(ssccId);
      setSelectedQmSiteId(siteId);
      setOpenRaiseQM(true);
    } finally {
      pendingQmOpenKeyRef.current = null;
      setPendingQmOpenKey(null);
    }
  }

  function openQmScopePicker(asn: string, ssccId: string) {
    if (isManifestInteractionLocked) return;

    if (!isQmSiteInboundEnabled) {
      alert('Site Inbound QM is currently disabled.', 'warning');
      return;
    }

    setPendingQmScopeSelection({ asn, ssccId });
    setOpenQmScopeDialog(true);
  }

  function getSelectedQmPackages(sourceManifest: ManifestProps | undefined): ManifestPackageProps[] {
    if (!sourceManifest) return [];
    return (
      sourceManifest.ssccs?.flatMap((sscc) => {
        if (selectedQmScope === 'sscc') {
          if (!selectedQmSsccId) {
            return [];
          }

          return sscc.ssccId === selectedQmSsccId ? sscc.packages : [];
        }

        if (sscc.packages.some((pkg) => pkg.asn === selectedASN)) {
          return sscc.packages.filter((pkg) => pkg.asn === selectedASN || isChildLineWithoutQuantityInput(pkg));
        }

        return [];
      }) || []
    );
  }

  const siteInboundManifestContext = useMemo<QmSiteInboundManifestContext>(() => {
    const affectedPackages =
      manifest?.ssccs?.flatMap((sscc) => {
        if (selectedQmScope === 'sscc') {
          if (!selectedQmSsccId) {
            return [];
          }

          return sscc.ssccId === selectedQmSsccId ? sscc.packages : [];
        }

        if (sscc.packages.some((pkg) => pkg.asn === selectedASN)) {
          return sscc.packages.filter((pkg) => pkg.asn === selectedASN || isChildLineWithoutQuantityInput(pkg));
        }

        return [];
      }) || [];
    const packagesByAsn = affectedPackages.reduce((acc, pkg) => {
      const asn = String(pkg.asn || '').trim();
      if (!asn || isChildLineWithoutQuantityInput(pkg)) return acc;

      const normalizedAsn = asn.toUpperCase();
      const packages = acc.get(normalizedAsn) || [];
      packages.push(pkg);
      acc.set(normalizedAsn, packages);
      return acc;
    }, new Map<string, ManifestPackageProps[]>());
    const asnMap = new Map<string, Map<string, QmSiteInboundAsnLine>>();

    packagesByAsn.forEach((asnPackages, normalizedAsn) => {
      const linesByKey = asnMap.get(normalizedAsn) || new Map();
      asnPackages.forEach((pkg) => {
        (pkg.inventoryLines || []).forEach((line) => {
          const key = `${line.orderLineId}:${line.partId}:${line.sourcePartId || ''}`;
          if (linesByKey.has(key)) return;
          linesByKey.set(key, {
            asn: normalizedAsn,
            poLineId: String(line.orderLineId || ''),
            itemId: String(line.partId || ''),
            sourcePartId: String(line.sourcePartId || ''),
            description: line.description || '',
            quantityOrdered: Number(line.quantityReceived || 0),
            quantityReceived: Number(line.quantityReceived || 0)
          });
        });
      });
      asnMap.set(normalizedAsn, linesByKey);
    });

    const getPackageQuantity = (pkg: ManifestPackageProps) => {
      const maxQuantity = Number(pkg.maxQuantity || 0);
      if (Number.isFinite(maxQuantity) && maxQuantity > 0) return maxQuantity;

      const quantity = Number(pkg.quantity || 0);
      if (Number.isFinite(quantity) && quantity > 0) return quantity;

      return 1;
    };
    const getPackageType = (pkg: ManifestPackageProps) =>
      String(
        pkg.packageType ||
          (pkg.inventoryLines || []).map((line) => String(line.packageType || '').trim()).find((value) => !!value) ||
          ''
      ).trim();

    const packageCount = Array.from(packagesByAsn.values()).reduce(
      (total, asnPackages) => total + asnPackages.reduce((asnTotal, pkg) => asnTotal + getPackageQuantity(pkg), 0),
      0
    );
    const packageTypeCounts = new Map<string, number>();
    const packageConfigurations = Array.from(packagesByAsn.values()).flatMap((asnPackages) =>
      asnPackages.flatMap((pkg) => {
        const packageType = getPackageType(pkg);
        const quantity = getPackageQuantity(pkg);
        if (packageType) {
          packageTypeCounts.set(packageType, (packageTypeCounts.get(packageType) || 0) + quantity);
        }

        return Array.from({ length: quantity }, () => ({ packageType }));
      })
    );
    const packageTypes = Array.from(packageTypeCounts.keys()).join(', ');
    return {
      targetScope: selectedQmScope,
      tripId: tripId || '',
      tripReference: tripReference || tripData?.reference || tripId || '',
      manifestId: manifest?.id || id || '',
      receivingWarehouseId: selectedQmSiteId,
      receivingWarehouseIds: selectedQmSiteId ? [selectedQmSiteId] : [],
      destinationBusinessName: manifestDestinationBusinessName,
      contactEmail: userEmail,
      packageCount: packageCount || affectedPackages.length || null,
      packageTypes,
      packageConfigurations: packageConfigurations.map((pkg, index) => ({
        packageNumber: index + 1,
        packageType: pkg.packageType
      })),
      asnDetails: Array.from(asnMap.entries()).map(([asn, linesByKey]) => ({
        asn,
        lines: Array.from(linesByKey.values())
      }))
    };
  }, [
    id,
    manifest,
    selectedASN,
    selectedQmSiteId,
    selectedQmScope,
    selectedQmSsccId,
    manifestDestinationBusinessName,
    tripData?.reference,
    tripId,
    tripReference,
    userEmail
  ]);

  function handleCloseQmScopePicker() {
    setOpenQmScopeDialog(false);
    setPendingQmScopeSelection(null);
  }

  function handleSelectQmScope(scope: 'sscc' | 'asn') {
    if (isManifestInteractionLocked) {
      return;
    }

    if (!pendingQmScopeSelection) {
      return;
    }

    const { asn, ssccId } = pendingQmScopeSelection;
    handleCloseQmScopePicker();
    if (scope === 'sscc') {
      void openQmForSscc(asn, ssccId);
      return;
    }
    void openQmForAsn(asn, ssccId);
  }

  function getPartialRowKey(pkg: ManifestPackageProps): string {
    const line = getPrimaryLine(pkg);
    const normalizedPackageId = String(pkg.id || '')
      .trim()
      .toLowerCase();
    const normalizedPoLineId = String(line?.orderLineId || '')
      .trim()
      .toLowerCase();
    const normalizedPartId = String(line?.partId || '')
      .trim()
      .toLowerCase();

    const rowIdentity =
      normalizedPoLineId || normalizedPartId
        ? `${normalizedPoLineId}::${normalizedPartId}`
        : `pkg::${normalizedPackageId}`;

    return `${String(pkg.asn || '')
      .trim()
      .toLowerCase()}::${rowIdentity}`;
  }

  function getPartialAsnLineKey(asn: string, line: ManifestPackageInvLineItemProps): string {
    return `line::${getAsnLineReceiveQuantityKey(asn, line)}`;
  }

  function isPartialPackageRowSelected(pkg: ManifestPackageProps): boolean {
    return Boolean(selectedPartialLineKeys[getPartialRowKey(pkg)]);
  }

  function isPartialAsnLineSelected(asn: string, line: ManifestPackageInvLineItemProps): boolean {
    return Boolean(selectedPartialLineKeys[getPartialAsnLineKey(asn, line)]);
  }

  function getPackageSsccId(pkg: ManifestPackageProps): string | null {
    return manifest?.ssccs.find((sscc) => sscc.packages.some((candidate) => candidate.id === pkg.id))?.ssccId || null;
  }

  function isPartialLineSelectedForSubmit(pkg: ManifestPackageProps, line: ManifestPackageInvLineItemProps): boolean {
    return isPartialPackageRowSelected(pkg) || isPartialAsnLineSelected(pkg.asn, line);
  }

  function hasPartialLineSelectionForPackage(pkg: ManifestPackageProps): boolean {
    return (pkg.inventoryLines || []).some((line) => isPartialAsnLineSelected(pkg.asn, line));
  }

  const allPartialStatusHistoryEntries = useMemo(
    () => Object.values(partialStatusHistoryByKey).flat(),
    [partialStatusHistoryByKey]
  );

  const successfulReceivedQuantityByAsnLineKey = useMemo(() => {
    const quantityByKey = new Map<string, number>();
    for (const entry of allPartialStatusHistoryEntries) {
      const status = String(entry.status || '')
        .trim()
        .toUpperCase();
      if (status !== 'RECEIVED_SUBMITTED' && status !== 'SUCCESS') {
        continue;
      }

      const normalizedAsn = String(entry.asn || '')
        .trim()
        .toLowerCase();
      const normalizedPoLineId = String(entry.poLineId || '')
        .trim()
        .toLowerCase();
      const normalizedItemId = String(entry.itemId || '')
        .trim()
        .toLowerCase();
      const quantity = Number(entry.receiveQuantity ?? entry.processedQuantity ?? entry.requestedQuantity ?? 0);
      const specificKey = `${normalizedAsn}::${normalizedPoLineId}::${normalizedItemId}`;
      const lineKey = `${normalizedAsn}::${normalizedPoLineId}::`;

      quantityByKey.set(specificKey, Number(quantityByKey.get(specificKey) || 0) + quantity);
      quantityByKey.set(lineKey, Number(quantityByKey.get(lineKey) || 0) + quantity);
    }

    return quantityByKey;
  }, [allPartialStatusHistoryEntries]);

  const partialHistoryByPackageId = useMemo(() => {
    const historyByPackageId = new Map<string, ManifestPartialReceiptLineStatus[]>();
    for (const entry of allPartialStatusHistoryEntries) {
      const packageId = String(entry.packageId || '')
        .trim()
        .toLowerCase();
      if (!packageId) {
        continue;
      }

      historyByPackageId.set(packageId, [...(historyByPackageId.get(packageId) || []), entry]);
    }

    for (const [packageId, entries] of historyByPackageId.entries()) {
      const seen = new Set<string>();
      historyByPackageId.set(
        packageId,
        entries
          .sort((a, b) => {
            const aTs = a.processedAt ? new Date(a.processedAt).getTime() : 0;
            const bTs = b.processedAt ? new Date(b.processedAt).getTime() : 0;
            return bTs - aTs;
          })
          .filter((entry) => {
            const normalizedStatus = String(entry.status || '')
              .trim()
              .toUpperCase();
            const isSavedSnapshotEntry =
              !entry.selectedForReceive &&
              normalizedStatus !== 'FAILED' &&
              !entry.errorCode &&
              !entry.errorMessage &&
              normalizedStatus !== 'NOT_SELECTED';
            const key = isSavedSnapshotEntry
              ? [
                  entry.sessionId,
                  entry.asn,
                  entry.packageId,
                  entry.status,
                  entry.packageQuantity,
                  entry.errorCode,
                  entry.errorMessage
                ]
                  .map((value) => String(value ?? ''))
                  .join('::')
              : [
                  entry.sessionId,
                  entry.packageId,
                  entry.poLineId,
                  entry.itemId,
                  entry.status,
                  entry.requestedQuantity,
                  entry.processedQuantity,
                  entry.receiveQuantity,
                  entry.errorCode,
                  entry.processedAt
                ]
                  .map((value) => String(value ?? ''))
                  .join('::');
            if (seen.has(key)) {
              return false;
            }
            seen.add(key);
            return true;
          })
      );
    }

    return historyByPackageId;
  }, [allPartialStatusHistoryEntries]);

  const partialSuccessPackageIds = useMemo(() => {
    const successPackageIds = new Set<string>();
    for (const pkg of manifest?.ssccs.flatMap((sscc) => sscc.packages) || []) {
      const packageId = String(pkg.id || '')
        .trim()
        .toLowerCase();
      const line = pkg.inventoryLines?.[0] || null;
      const normalizedAsn = String(pkg.asn || '')
        .trim()
        .toLowerCase();
      const normalizedPoLineId = String(line?.orderLineId || '')
        .trim()
        .toLowerCase();
      const normalizedPartId = String(line?.partId || '')
        .trim()
        .toLowerCase();
      const rowIdentity =
        normalizedPoLineId || normalizedPartId ? `${normalizedPoLineId}::${normalizedPartId}` : `pkg::${packageId}`;
      const rowKey = `${normalizedAsn}::${rowIdentity}`;
      const history =
        (packageId ? partialHistoryByPackageId.get(packageId) : null) || partialStatusHistoryByKey[rowKey] || [];
      if (packageId && isPartialSuccessHistory(history)) {
        successPackageIds.add(packageId);
      }
    }

    return successPackageIds;
  }, [manifest, partialHistoryByPackageId, partialStatusHistoryByKey]);

  const partialSuccessAsnGroupKeys = useMemo(() => {
    const successGroupKeys = new Set<string>();
    for (const sscc of manifest?.ssccs || []) {
      for (const pkg of sscc.packages) {
        const packageId = String(pkg.id || '')
          .trim()
          .toLowerCase();
        if (partialSuccessPackageIds.has(packageId)) {
          successGroupKeys.add(
            `${sscc.ssccId}::${String(pkg.asn || '')
              .trim()
              .toUpperCase()}`
          );
        }
      }
    }

    return successGroupKeys;
  }, [manifest, partialSuccessPackageIds]);

  function isPartialSuccess(pkg: ManifestPackageProps): boolean {
    const lineLevelSuccess = isPackageFullyReceivedByAsnLineStatus(pkg);
    if (lineLevelSuccess !== null) {
      return lineLevelSuccess;
    }

    const normalizedPackageId = String(pkg.id || '')
      .trim()
      .toLowerCase();
    if (normalizedPackageId && partialSuccessPackageIds.has(normalizedPackageId)) {
      return true;
    }

    const normalizedAsn = String(pkg.asn || '')
      .trim()
      .toUpperCase();
    const ssccId = getPackageSsccId(pkg);
    return Boolean(ssccId && partialSuccessAsnGroupKeys.has(`${ssccId}::${normalizedAsn}`));
  }

  function isPackageFullyReceivedByAsnLineStatus(pkg: ManifestPackageProps): boolean | null {
    const receivableLines = (pkg.inventoryLines || []).filter((line) => getDefaultAsnLineReceiveQuantity(line) > 0);
    if (receivableLines.length === 0) {
      return null;
    }

    const ssccId = getPackageSsccId(pkg);
    return receivableLines.every(
      (line) => !getOpenQmMatchForLine(pkg.asn, line, ssccId) && isAsnLinePartialSuccess(pkg.asn, line)
    );
  }

  function isPackagePartiallyReceivedByAsnLineStatus(pkg: ManifestPackageProps): boolean {
    const receivableLines = (pkg.inventoryLines || []).filter((line) => getDefaultAsnLineReceiveQuantity(line) > 0);
    if (receivableLines.length === 0) {
      return false;
    }

    const receivedLineCount = receivableLines.filter((line) => isAsnLinePartialSuccess(pkg.asn, line)).length;
    return receivedLineCount > 0 && receivedLineCount < receivableLines.length;
  }

  function getPackageItemStatusLabel(
    pkg: ManifestPackageProps | undefined,
    openQmMatch?: ReceiveManifestQmMatchResult | null
  ): string {
    if (!pkg) return '';
    if (openQmMatch) return ManifestPackageStatus.qm;
    if (Number(pkg.maxQuantity || 0) > 0 && Number(pkg.quantity || 0) >= Number(pkg.maxQuantity || 0)) {
      return ManifestPackageStatus.received;
    }
    if (isPackagePartiallyReceivedByAsnLineStatus(pkg)) return ManifestPackageStatus.partial;
    return pkg.receivedStatus ?? '';
  }

  function getPackageItemStatusFilterValue(pkg: ManifestPackageProps, sscc: ManifestSSCCProps): string {
    const openQmMatch = getOpenQmMatchesForPackage(pkg, sscc.ssccId)[0] || null;
    return getPackageItemStatusLabel(pkg, openQmMatch) || ManifestPackageStatus.notReceived;
  }

  function isAsnLinePartialSuccess(asn: string | undefined, line: ManifestPackageInvLineItemProps): boolean {
    if (!asn) {
      return false;
    }

    const receivedQuantity = getSuccessfulReceivedQuantityForAsnLine(asn, line);
    return receivedQuantity > 0 && receivedQuantity >= getDefaultAsnLineReceiveQuantity(line);
  }

  function isPartialSuccessHistory(history: ManifestPartialReceiptLineStatus[]): boolean {
    const latestPartialStatus = history.find((entry) => {
      const status = String(entry.status || '')
        .trim()
        .toUpperCase();
      return status === 'RECEIVED_SUBMITTED' || status === 'SUCCESS';
    });
    const latestStatus = String(latestPartialStatus?.status || '')
      .trim()
      .toUpperCase();
    if (latestStatus !== 'RECEIVED_SUBMITTED' && latestStatus !== 'SUCCESS') {
      return false;
    }

    const sourceQuantity = Number(latestPartialStatus?.receivedQuantity ?? latestPartialStatus?.requestedQuantity ?? 0);
    const processedQuantity = history.reduce((total, entry) => {
      const status = String(entry.status || '')
        .trim()
        .toUpperCase();
      if (status !== 'RECEIVED_SUBMITTED' && status !== 'SUCCESS') {
        return total;
      }

      return total + Number(entry.receiveQuantity ?? entry.processedQuantity ?? entry.requestedQuantity ?? 0);
    }, 0);

    return sourceQuantity <= 0 || processedQuantity >= sourceQuantity;
  }

  function isIncorrectQuantityQmMatch(match: ReceiveManifestQmMatchResult): boolean {
    return [match.reasonCode, match.reasonLabel].some((candidate) => {
      const normalized = String(candidate || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      return normalized === 'incorrectqty' || normalized === 'incorrectquantity';
    });
  }

  function isOpenBlockingQmMatch(match: ReceiveManifestQmMatchResult): boolean {
    const status = String(match.status || '').toUpperCase();
    return (status === 'CREATED' || status === 'PENDING') && !isIncorrectQuantityQmMatch(match);
  }

  function getQmMatchesForPackage(
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult[] {
    const matchesByQm = new Map<string, ReceiveManifestQmMatchResult>();
    const asnMatches = qmMatchesByLineKeyRef.current[getQmMatchAsnKey(pkg.asn, sscc)] || [];
    asnMatches.forEach((asnMatch) => {
      if (asnMatch?.qmId) {
        matchesByQm.set(String(asnMatch.qmId || asnMatch.qmNumber), asnMatch);
      }
    });

    for (const line of pkg.inventoryLines || []) {
      getQmMatchesForLine(pkg.asn, line, sscc).forEach((match) => {
        matchesByQm.set(String(match.qmId || match.qmNumber), match);
      });
    }

    return Array.from(matchesByQm.values());
  }

  function getOpenQmMatchesForPackage(
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult[] {
    return getQmMatchesForPackage(pkg, sscc).filter((match) => {
      const status = String(match.status || '').toUpperCase();
      return status === 'CREATED' || status === 'PENDING';
    });
  }

  function getOpenQmMatchForLine(
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult | null {
    return getOpenQmMatchesForLine(asn, line, sscc)[0] || null;
  }

  function getOpenQmMatchesForLine(
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult[] {
    const matchesByQm = new Map<string, ReceiveManifestQmMatchResult>();
    const asnMatches = qmMatchesByLineKeyRef.current[getQmMatchAsnKey(asn, sscc)] || [];
    asnMatches.forEach((asnMatch) => {
      if (asnMatch?.qmId) {
        const status = String(asnMatch.status || '').toUpperCase();
        if (status === 'CREATED' || status === 'PENDING') {
          matchesByQm.set(String(asnMatch.qmId || asnMatch.qmNumber), asnMatch);
        }
      }
    });

    for (const key of getQmMatchKeysForLine(asn, line, sscc)) {
      const matches =
        qmMatchesByLineKeyRef.current[getQmMatchLineKey(key.asn, key.poLineId, key.itemId, key.sscc)] || [];
      matches.forEach((match) => {
        if (!match?.qmId) return;
        const status = String(match.status || '').toUpperCase();
        if (status !== 'CREATED' && status !== 'PENDING') return;
        matchesByQm.set(String(match.qmId || match.qmNumber), match);
      });
    }

    return Array.from(matchesByQm.values());
  }

  function getOpenQmMatchesForSscc(sscc: ManifestSSCCProps): ReceiveManifestQmMatchResult[] {
    const matchesByQm = new Map<string, ReceiveManifestQmMatchResult>();
    for (const pkg of sscc.packages) {
      getOpenQmMatchesForPackage(pkg, sscc.ssccId).forEach((match) => {
        matchesByQm.set(String(match.qmId || match.qmNumber), match);
      });
    }

    return Array.from(matchesByQm.values());
  }

  function hasOpenQmInManifest(sourceManifest: ManifestProps | undefined): boolean {
    return Boolean(
      sourceManifest?.ssccs.some((sscc) =>
        sscc.packages.some((pkg) =>
          (pkg.inventoryLines || []).some((line) => getOpenQmMatchForLine(pkg.asn, line, sscc.ssccId))
        )
      )
    );
  }

  function getOpenReceiptAttentionQmMatch(
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ): ReceiveManifestQmMatchResult | null {
    return getQmMatchesForPackage(pkg, sscc).find((match) => isOpenBlockingQmMatch(match)) || null;
  }

  function hasReceivableUnblockedLine(pkg: ManifestPackageProps, sscc?: string | number | null): boolean {
    return (pkg.inventoryLines || []).some(
      (line) => getRemainingAsnLineReceiveQuantity(pkg.asn, line) > 0 && !getOpenQmMatchForLine(pkg.asn, line, sscc)
    );
  }

  function hasOpenQmOnAsnLines(
    asn: string | undefined,
    lines: ManifestPackageInvLineItemProps[],
    sscc?: string | number | null
  ): boolean {
    return Boolean(asn && lines.some((line) => getOpenQmMatchForLine(asn, line, sscc)?.qmId));
  }

  function getSelectedUnblockedPartialLines(pkg: ManifestPackageProps): ManifestPackageInvLineItemProps[] {
    const ssccId = getPackageSsccId(pkg);
    return (pkg.inventoryLines || []).filter(
      (line) =>
        isPartialLineSelectedForSubmit(pkg, line) &&
        getRemainingAsnLineReceiveQuantity(pkg.asn, line) > 0 &&
        !getOpenQmMatchForLine(pkg.asn, line, ssccId)
    );
  }

  function buildSelectedPartialSubmitManifest(
    sourceManifest: ManifestProps,
    packages: ManifestPackageProps[]
  ): ManifestProps {
    const selectedPackageIds = new Set(packages.map((pkg) => pkg.id));

    return {
      ...sourceManifest,
      ssccs: sourceManifest.ssccs
        .map((sscc) => ({
          ...sscc,
          packages: sscc.packages
            .filter((pkg) => selectedPackageIds.has(pkg.id))
            .map((pkg) => {
              const selectedLines = getSelectedUnblockedPartialLines(pkg);

              return {
                ...pkg,
                receivedStatus: ManifestPackageStatus.received,
                inventoryLines: selectedLines
              };
            })
            .filter((pkg) => (pkg.inventoryLines || []).length > 0)
        }))
        .filter((sscc) => sscc.packages.length > 0)
    };
  }

  function keepPositiveReceiptQuantities(
    items: Partial<ReceiptEventPackageProps>[]
  ): Partial<ReceiptEventPackageProps>[] {
    return items
      .map((entry) => ({
        ...entry,
        items: (entry.items || []).filter((item) => Number(item.quantityReceived || 0) > 0)
      }))
      .filter((entry) => (entry.items?.length || 0) > 0);
  }

  function getLatestPartialStatus(
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ): ManifestPartialReceiptLineStatus | null {
    return getPartialHistoryForPackage(pkg, sscc)[0] || null;
  }

  function isFailedPartialStatus(entry: ManifestPartialReceiptLineStatus | null): boolean {
    return String(entry?.status || '').toUpperCase() === 'FAILED';
  }

  async function openQmContext(match: ReceiveManifestQmMatchResult) {
    if (!match.qmId) return;
    await openQmDetails(match.qmId);
  }

  function closeQmPicker() {
    setQmPickerAnchorEl(null);
    setQmPickerMatches([]);
  }

  function renderQmCheckboxAreaChip(
    match: ReceiveManifestQmMatchResult | null,
    options: {
      aggregateCount?: number;
      aggregateMatches?: ReceiveManifestQmMatchResult[];
    } = {}
  ) {
    if (!match?.qmId) return null;

    const aggregateCount = Number(options.aggregateCount || 0);
    const isAggregate = aggregateCount > 1;
    const aggregateMatches = (options.aggregateMatches || []).filter((candidate) => candidate.qmId);
    const canOpenAggregatePicker = isAggregate && aggregateMatches.length > 0;
    const openAggregatePicker = (event: MouseEvent<HTMLElement>) => {
      if (!canOpenAggregatePicker) return;
      setQmPickerAnchorEl(event.currentTarget);
      setQmPickerMatches(aggregateMatches);
    };
    const visibleLabel = isAggregate ? `${aggregateCount} QMs` : getQmReferenceLabel(match);
    const tooltipLabel = isAggregate
      ? canOpenAggregatePicker
        ? `Show ${aggregateCount} QMs`
        : `${aggregateCount} QMs on ASN lines`
      : visibleLabel
        ? `QM ${visibleLabel}`
        : 'QM';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Tooltip title={tooltipLabel} enterTouchDelay={0}>
          <Chip
            size="small"
            label={visibleLabel}
            color="warning"
            variant="outlined"
            onClick={
              isAggregate ? (canOpenAggregatePicker ? openAggregatePicker : undefined) : () => void openQmContext(match)
            }
            sx={{
              maxWidth: 82,
              cursor: isAggregate && !canOpenAggregatePicker ? 'default' : 'pointer',
              fontSize: '0.68rem',
              height: 22,
              '& .MuiChip-label': {
                px: 0.65,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
        </Tooltip>
      </Box>
    );
  }

  function getQmReferenceLabel(match: ReceiveManifestQmMatchResult | null): string {
    const rawLabel = String(match?.qmNumber || match?.qmId || '').trim();
    return rawLabel.replace(/^QM\s+/i, '');
  }

  function buildQmMatchMap(matches: ReceiveManifestQmMatchResult[]): Record<string, ReceiveManifestQmMatchResult[]> {
    return matches.reduce<Record<string, ReceiveManifestQmMatchResult[]>>((acc, match) => {
      const key = getQmMatchLineKey(match.asn, match.poLineId, match.itemId, match.sscc);
      acc[key] = [...(acc[key] || []), match];
      return acc;
    }, {});
  }

  function applyQmMatchMap(
    nextMatchesByLineKey: Record<string, ReceiveManifestQmMatchResult[]>,
    mode: 'replace' | 'merge' = 'replace'
  ) {
    const mergedMatchesByLineKey =
      mode === 'merge'
        ? Object.entries(nextMatchesByLineKey).reduce<Record<string, ReceiveManifestQmMatchResult[]>>(
            (acc, [key, matches]) => {
              const mergedByQm = new Map<string, ReceiveManifestQmMatchResult>();
              [...(acc[key] || []), ...matches].forEach((match) => {
                mergedByQm.set(String(match.qmId || match.qmNumber || `${match.asn}-${match.poLineId}`), match);
              });
              acc[key] = Array.from(mergedByQm.values());
              return acc;
            },
            { ...qmMatchesByLineKeyRef.current }
          )
        : nextMatchesByLineKey;
    qmMatchesByLineKeyRef.current = mergedMatchesByLineKey;
    setQmMatchesByLineKey(mergedMatchesByLineKey);
  }

  async function findBlockingQmForPartialSubmit(packages: ManifestPackageProps[]): Promise<string | null> {
    const keys = packages.flatMap((pkg) => getQmMatchKeysForPackages([pkg], getPackageSsccId(pkg)));

    const matches = await getReceiveManifestQmMatchesRequest(keys);
    const matchByLineKey = new Map(Object.entries(buildQmMatchMap(matches)));
    applyQmMatchMap(buildQmMatchMap(matches), 'merge');
    const blockingQm = matches.find((match) => match.qmId && isOpenBlockingQmMatch(match));
    const hasUnblockedReceiveQuantity = packages.some((pkg) =>
      (pkg.inventoryLines || []).some((line) => {
        const match = getQmMatchFromMap(matchByLineKey, pkg.asn, line, getPackageSsccId(pkg));
        return (
          isPartialLineSelectedForSubmit(pkg, line) &&
          getAsnLineReceiveQuantity(pkg.asn, line) > 0 &&
          !(match && isOpenBlockingQmMatch(match))
        );
      })
    );
    if (blockingQm && !hasUnblockedReceiveQuantity) {
      return `Partial submit blocked by open QM ${blockingQm.qmNumber || blockingQm.qmId}. Resolve it or use Incorrect Qty for partial PO receipt.`;
    }

    for (const pkg of packages) {
      for (const line of pkg.inventoryLines || []) {
        if (!isPartialLineSelectedForSubmit(pkg, line)) {
          continue;
        }

        const match = getQmMatchFromMap(matchByLineKey, pkg.asn, line, getPackageSsccId(pkg));
        if (match && isOpenBlockingQmMatch(match)) {
          continue;
        }

        const defaultReceiveQuantity = getDefaultAsnLineReceiveQuantity(line);
        const selectedReceiveQuantity = getAsnLineReceiveQuantity(pkg.asn, line);
        const priorReceivedQuantity = getSuccessfulReceivedQuantityForAsnLine(pkg.asn, line);
        if (selectedReceiveQuantity >= defaultReceiveQuantity) {
          continue;
        }

        if (priorReceivedQuantity > 0) {
          continue;
        }

        const status = String(match?.status || '').toUpperCase();
        const hasOpenIncorrectQuantityQm =
          Boolean(match?.qmId) && (status === 'CREATED' || status === 'PENDING') && isIncorrectQuantityQmMatch(match!);

        if (!hasOpenIncorrectQuantityQm) {
          return 'Reduced ASN line receipt quantity requires an open Incorrect Qty QM for that PO line.';
        }
      }
    }

    return null;
  }

  function isPackageEligibleForCreateSiteReceipt(
    pkg: ManifestPackageProps,
    isTransportBookingPackage: boolean,
    hasFtlAsn: boolean
  ): boolean {
    if (!hasFtlAsn || isTransportBookingPackage || isVarious(pkg)) {
      return false;
    }

    if (pkg.receivedStatus === ManifestPackageStatus.qm || pkg.receivedStatus === ManifestPackageStatus.qm_pending) {
      return false;
    }

    if (
      pkg.receivedStatus === ManifestPackageStatus.direct ||
      pkg.receivedStatus === ManifestPackageStatus.direct_pending
    ) {
      return false;
    }

    if (!getPrimaryLine(pkg)) {
      return false;
    }

    return Number(pkg.quantity || 0) > 0;
  }

  function togglePartialLineSelection(pkg: ManifestPackageProps, checked: boolean) {
    if (isManifestInteractionLocked) return;

    const rowKey = getPartialRowKey(pkg);
    setSelectedPartialLineKeys((prev) => {
      const next = { ...prev };
      if (checked) {
        next[rowKey] = true;
      } else {
        delete next[rowKey];
      }
      return next;
    });
  }

  function togglePartialAsnLineSelection(asn: string, line: ManifestPackageInvLineItemProps, checked: boolean) {
    if (isManifestInteractionLocked) return;

    const lineKey = getPartialAsnLineKey(asn, line);
    setSelectedPartialLineKeys((prev) => {
      const next = { ...prev };
      if (checked) {
        next[lineKey] = true;
      } else {
        delete next[lineKey];
      }
      return next;
    });
  }

  function hasPartialStatusError(line: ManifestPartialReceiptLineStatus): boolean {
    const normalizedStatus = String(line.status || '')
      .trim()
      .toUpperCase();
    return normalizedStatus === 'FAILED' || Boolean(line.errorCode || line.errorMessage);
  }

  function getSavedHistoryState(line: ManifestPartialReceiptLineStatus): string {
    const normalizedStatus = String(line.status || '')
      .trim()
      .toUpperCase();
    const quantity = line.packageQuantity ?? line.receiveQuantity ?? line.processedQuantity ?? line.requestedQuantity;
    return `${normalizedStatus}::${quantity ?? ''}`;
  }

  function isSavedSnapshotHistoryLine(line: ManifestPartialReceiptLineStatus): boolean {
    const normalizedStatus = String(line.status || '')
      .trim()
      .toUpperCase();
    return (
      !line.selectedForReceive &&
      !hasPartialStatusError(line) &&
      normalizedStatus !== 'NOT_SELECTED' &&
      normalizedStatus !== 'QM_RAISED' &&
      normalizedStatus !== 'QM RAISED'
    );
  }

  function isReceiptResultHistoryLine(line: ManifestPartialReceiptLineStatus): boolean {
    const normalizedStatus = String(line.status || '')
      .trim()
      .toUpperCase();
    return (
      line.selectedForReceive &&
      (normalizedStatus === 'RECEIVED_SUBMITTED' ||
        normalizedStatus === 'SUCCESS' ||
        normalizedStatus === 'FAILED' ||
        hasPartialStatusError(line))
    );
  }

  function isSavedSnapshotQuantityChange(line: ManifestPartialReceiptLineStatus): boolean {
    if (!isSavedSnapshotHistoryLine(line) || line.packageQuantity === null || line.packageQuantity === undefined) {
      return false;
    }

    const sourceQuantity = line.receivedQuantity ?? line.orderedQuantity;
    if (sourceQuantity === null || sourceQuantity === undefined) {
      const normalizedStatus = String(line.status || '')
        .trim()
        .toUpperCase();
      return Number(line.packageQuantity) > 0 || normalizedStatus !== 'NOT RECEIVED';
    }

    return Number(line.packageQuantity) !== Number(sourceQuantity);
  }

  function hasSavedAsnHistoryChange(lines: ManifestPartialReceiptLineStatus[]): boolean {
    const savedLines = lines.filter(isSavedSnapshotHistoryLine);

    if (savedLines.length < 2) {
      return false;
    }

    const firstState = getSavedHistoryState(savedLines[0]);
    return savedLines.some((line) => getSavedHistoryState(line) !== firstState);
  }

  function buildQmRaisedHistoryEntry(
    match: ReceiveManifestQmMatchResult,
    pkg: ManifestPackageProps
  ): ManifestPartialReceiptLineStatus {
    const targetType = String(match.targetType || '')
      .trim()
      .toUpperCase();
    const line =
      (pkg.inventoryLines || []).find(
        (candidate) =>
          String(candidate.orderLineId || '').trim() === String(match.poLineId || '').trim() &&
          (!match.itemId ||
            String(candidate.partId || '').trim() === String(match.itemId || '').trim() ||
            String(candidate.sourcePartId || '').trim() === String(match.itemId || '').trim())
      ) || getPrimaryLine(pkg);
    return {
      id: `qm-${match.qmId || match.qmNumber || `${match.asn}-${match.poLineId}`}`,
      sessionId: `qm-${match.qmId || match.qmNumber || 'unknown'}`,
      manifestId: manifest?.id || '',
      triggeredBy: match.createdBy || match.updatedBy || null,
      asn: match.asn || pkg.asn || null,
      poLineId: String(match.poLineId || line?.orderLineId || '') || null,
      itemId: match.itemId || (line?.partId ? String(line.partId) : null),
      sourcePartId: line?.sourcePartId || null,
      lineDescription: line?.description || null,
      packageId: pkg.id || null,
      selectedForReceive: false,
      requestedQuantity: null,
      processedQuantity: null,
      receiveQuantity: null,
      packageQuantity: null,
      orderedQuantity: null,
      receivedQuantity: null,
      status: 'QM_RAISED',
      errorCode: null,
      errorMessage: match.qmNumber ? `QM ${match.qmNumber}` : match.reasonLabel || null,
      processedAt: match.createdAt || match.updatedAt || null,
      scopeLabel: targetType === 'SSCC' ? 'SSCC' : targetType === 'ASN' ? 'ASN QM' : 'ASN Line QM'
    };
  }

  function buildQmClosedHistoryEntry(
    match: ReceiveManifestQmMatchResult,
    pkg: ManifestPackageProps
  ): ManifestPartialReceiptLineStatus | null {
    const closedAt = match.closedAt || (match.status === 'CLOSED' ? match.updatedAt : null);
    if (match.status !== 'CLOSED' || !closedAt) {
      return null;
    }

    const raisedEntry = buildQmRaisedHistoryEntry(match, pkg);
    const closureDetail = [match.qmNumber ? `QM ${match.qmNumber}` : '', match.closureLabel || match.closureCode || '']
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' - ');

    return {
      ...raisedEntry,
      id: `qm-closed-${match.qmId || match.qmNumber || `${match.asn}-${match.poLineId}`}`,
      sessionId: `qm-closed-${match.qmId || match.qmNumber || 'unknown'}`,
      triggeredBy: match.updatedBy || null,
      status: 'QM_CLOSED',
      errorMessage: closureDetail || raisedEntry.errorMessage,
      processedAt: closedAt
    };
  }

  function buildQmHistoryEntries(match: ReceiveManifestQmMatchResult, pkg: ManifestPackageProps) {
    const closedEntry = buildQmClosedHistoryEntry(match, pkg);
    return [buildQmRaisedHistoryEntry(match, pkg), closedEntry].filter(
      (entry): entry is ManifestPartialReceiptLineStatus => entry !== null
    );
  }

  function mergeHistoryEntries(entries: ManifestPartialReceiptLineStatus[]): ManifestPartialReceiptLineStatus[] {
    return dedupeHistoryEntries(entries).sort((a, b) => {
      const aTs = a.processedAt ? new Date(a.processedAt).getTime() : 0;
      const bTs = b.processedAt ? new Date(b.processedAt).getTime() : 0;
      return bTs - aTs;
    });
  }

  function getPartialHistoryForPackage(
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ): ManifestPartialReceiptLineStatus[] {
    const normalizedPackageId = String(pkg.id || '')
      .trim()
      .toLowerCase();
    const qmHistoryEntries = getQmMatchesForPackage(pkg, sscc)
      .filter((match) => match.qmId)
      .flatMap((match) => buildQmHistoryEntries(match, pkg));

    if (normalizedPackageId) {
      const packageHistory = partialHistoryByPackageId.get(normalizedPackageId) || [];

      if (packageHistory.length > 0) {
        return qmHistoryEntries.length > 0
          ? mergeHistoryEntries([...qmHistoryEntries, ...packageHistory])
          : packageHistory;
      }
    }

    const history = dedupeHistoryEntries(partialStatusHistoryByKey[getPartialRowKey(pkg)] || []);
    return qmHistoryEntries.length > 0 ? mergeHistoryEntries([...qmHistoryEntries, ...history]) : history;
  }

  function getHistoryDialogContext(
    ssccId: string,
    pkg: ManifestPackageProps,
    packageLabel: string
  ): HistoryDialogContext {
    const line = getPrimaryLine(pkg);
    return {
      ssccId,
      asn: String(pkg.asn || ''),
      poNumber: String(pkg.poNumber || ''),
      packageLabel,
      packageType: String(pkg.packageType || ''),
      stockStatus: isStockPurchaseType(pkg) ? 'Stock' : 'Standard Consume',
      status: String(pkg.receivedStatus || ''),
      poLineId: String(line?.orderLineId || ''),
      itemId: String(line?.partId || ''),
      sourcePartId: String(line?.sourcePartId || ''),
      lineDescription: String(line?.description || ''),
      packageId: String(pkg.id || '')
    };
  }

  function getHistoryManifestLine(entry: ManifestPartialReceiptLineStatus): ManifestPackageInvLineItemProps | null {
    const entryPackageId = String(entry.packageId || '').trim();
    const entryAsn = String(entry.asn || '').trim();
    const entryPoLineId = String(entry.poLineId || '').trim();
    const entryItemId = String(entry.itemId || '').trim();
    const candidatePackages =
      manifest?.ssccs
        .flatMap((sscc) => sscc.packages)
        .filter((pkg) => {
          if (entryPackageId && String(pkg.id || '').trim() === entryPackageId) {
            return true;
          }

          return entryAsn && String(pkg.asn || '').trim() === entryAsn;
        }) || [];

    return (
      candidatePackages
        .flatMap((pkg) => pkg.inventoryLines || [])
        .find((line) => {
          const poLineMatches = !entryPoLineId || String(line.orderLineId || '').trim() === entryPoLineId;
          const itemMatches = !entryItemId || String(line.partId || '').trim() === entryItemId;
          return poLineMatches && itemMatches;
        }) || null
    );
  }

  function getHistorySourcePartId(entry: ManifestPartialReceiptLineStatus): string {
    return (
      entry.sourcePartId || getHistoryManifestLine(entry)?.sourcePartId || expandedHistoryContext?.sourcePartId || '-'
    );
  }

  function getHistoryLineDescription(entry: ManifestPartialReceiptLineStatus): string {
    return (
      entry.lineDescription ||
      getHistoryManifestLine(entry)?.description ||
      expandedHistoryContext?.lineDescription ||
      '-'
    );
  }

  function getHistoryStatusLabel(entry: ManifestPartialReceiptLineStatus): string {
    const normalizedStatus = String(entry.status || '')
      .trim()
      .toUpperCase();

    if (normalizedStatus === 'QM_RAISED' || normalizedStatus === 'QM RAISED') {
      return 'QM Raised';
    }

    if (normalizedStatus === 'QM_CLOSED' || normalizedStatus === 'QM CLOSED') {
      return 'QM Closed';
    }

    if (
      normalizedStatus === 'DIRECT_RECEIPTED' ||
      normalizedStatus === 'DIRECT RECEIPTED' ||
      normalizedStatus === 'DIRECT_PENDING' ||
      normalizedStatus === 'DIRECT PENDING'
    ) {
      return 'Direct Receipted';
    }

    if (entry.selectedForReceive && normalizedStatus === 'RECEIVED_SUBMITTED') {
      return 'Received + Submitted';
    }

    if (entry.selectedForReceive && normalizedStatus === 'SUCCESS') {
      return 'Submitted';
    }

    return entry.status || '-';
  }

  function getHistoryScopeLabel(entry: ManifestPartialReceiptLineStatus): string {
    if (entry.scopeLabel) {
      return entry.scopeLabel;
    }

    const normalizedStatus = String(entry.status || '')
      .trim()
      .toUpperCase();

    if (normalizedStatus === 'QM_RAISED' || normalizedStatus === 'QM RAISED') {
      return entry.poLineId || entry.itemId ? 'ASN Line QM' : 'ASN QM';
    }

    if (normalizedStatus === 'QM_CLOSED' || normalizedStatus === 'QM CLOSED') {
      return entry.poLineId || entry.itemId ? 'ASN Line QM' : 'ASN QM';
    }

    if (isReceiptResultHistoryLine(entry) || hasPartialStatusError(entry)) {
      return 'ASN Line';
    }

    if (isSavedSnapshotHistoryLine(entry)) {
      return 'Package Qty';
    }

    return '-';
  }

  function formatHistoryUser(userValue: string | null): string {
    const value = String(userValue || '').trim();
    if (!value) {
      return 'Unknown';
    }

    return value.includes('@') ? value.split('@')[0] : value;
  }

  function renderHistoryValue(value: number | string | null | undefined): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  function getHistoryErrorDetail(entry: ManifestPartialReceiptLineStatus | null | undefined): string {
    if (!entry) return '';

    return [entry.errorCode, entry.errorMessage]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(': ');
  }

  function getHistoryEntryDedupeKey(entry: ManifestPartialReceiptLineStatus): string {
    if (isSavedSnapshotHistoryLine(entry)) {
      return [
        entry.sessionId,
        entry.asn,
        entry.packageId,
        entry.status,
        entry.packageQuantity,
        entry.errorCode,
        entry.errorMessage
      ]
        .map((value) => String(value ?? ''))
        .join('::');
    }

    return [
      entry.sessionId,
      entry.processedAt,
      entry.asn,
      entry.poLineId,
      entry.itemId,
      entry.sourcePartId,
      entry.lineDescription,
      entry.packageId,
      entry.status,
      entry.packageQuantity,
      entry.requestedQuantity,
      entry.processedQuantity,
      entry.receiveQuantity,
      entry.errorCode,
      entry.errorMessage
    ]
      .map((value) => String(value ?? ''))
      .join('::');
  }

  function dedupeHistoryEntries(entries: ManifestPartialReceiptLineStatus[]): ManifestPartialReceiptLineStatus[] {
    const seen = new Set<string>();
    return entries.filter((entry) => {
      const key = getHistoryEntryDedupeKey(entry);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  function closeHistoryDialog() {
    setExpandedHistoryRowKey(null);
    setExpandedHistoryEntries([]);
    setExpandedHistoryContext(null);
  }

  function renderHistoryButton(
    rowKey: string,
    historyEntries: ManifestPartialReceiptLineStatus[],
    context: HistoryDialogContext
  ) {
    if (historyEntries.length === 0) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Tooltip title={expandedHistoryRowKey === rowKey ? 'Hide ASN History' : 'Show ASN History'} enterTouchDelay={0}>
          <IconButton
            size="small"
            disabled={isManifestInteractionLocked}
            onClick={() => {
              if (expandedHistoryRowKey === rowKey) {
                closeHistoryDialog();
                return;
              }

              setExpandedHistoryRowKey(rowKey);
              setExpandedHistoryEntries(historyEntries);
              setExpandedHistoryContext(context);
            }}
            sx={{
              border: `1px solid ${theme.palette.info.main}`,
              borderRadius: '8px',
              width: 32,
              height: 32,
              padding: 0
            }}>
            <ArticleOutlinedIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  function buildPartialStatusHistoryMap(lines: ManifestPartialReceiptLineStatus[]) {
    const relevantLines = lines.filter(
      (line) => line.selectedForReceive || hasPartialStatusError(line) || isSavedSnapshotHistoryLine(line)
    );
    const grouped = relevantLines.reduce(
      (acc, line) => {
        const key = `${String(line.asn || '')
          .trim()
          .toLowerCase()}::${(() => {
          const normalizedPoLineId = String(line.poLineId || '')
            .trim()
            .toLowerCase();
          const normalizedItemId = String(line.itemId || '')
            .trim()
            .toLowerCase();
          const normalizedPackageId = String(line.packageId || '')
            .trim()
            .toLowerCase();

          if (normalizedPoLineId || normalizedItemId) {
            return `${normalizedPoLineId}::${normalizedItemId}`;
          }

          return `pkg::${normalizedPackageId}`;
        })()}`;
        acc[key] = [...(acc[key] || []), line];
        return acc;
      },
      {} as Record<string, ManifestPartialReceiptLineStatus[]>
    );

    for (const key of Object.keys(grouped)) {
      grouped[key] = grouped[key].sort((a, b) => {
        const aTs = a.processedAt ? new Date(a.processedAt).getTime() : 0;
        const bTs = b.processedAt ? new Date(b.processedAt).getTime() : 0;
        return bTs - aTs;
      });

      const hasSavedChange = hasSavedAsnHistoryChange(grouped[key]);
      grouped[key] = grouped[key].filter(
        (line) =>
          line.selectedForReceive ||
          hasPartialStatusError(line) ||
          ((hasSavedChange || isSavedSnapshotQuantityChange(line)) && isSavedSnapshotHistoryLine(line))
      );

      if (grouped[key].length === 0) {
        delete grouped[key];
      } else {
        grouped[key] = dedupeHistoryEntries(grouped[key]);
      }
    }

    return grouped;
  }

  async function refreshPartialStatusHistoryForManifest(manifestId: string, applyToCurrentManifest: boolean) {
    const statusResult = await getManifestPartialReceiptStatusRequest(manifestId);
    const latestLines = statusResult.lines || [];
    setPartialStatusHistoryByKey(buildPartialStatusHistoryMap(latestLines));

    if (applyToCurrentManifest) {
      setManifest((prev) => {
        if (!prev || prev.id !== manifestId) {
          return prev;
        }

        return applyPartialReceiptQuantitiesToManifest(prev, latestLines);
      });
    }
  }

  async function refreshQmMatchesForManifestLines(sourceManifest: ManifestProps | undefined) {
    const refreshSeq = qmMatchRefreshSeqRef.current + 1;
    qmMatchRefreshSeqRef.current = refreshSeq;

    if (!sourceManifest?.ssccs?.length) {
      qmMatchesByLineKeyRef.current = {};
      setQmMatchesByLineKey({});
      return;
    }

    const keys = sourceManifest.ssccs.flatMap((sscc) => getQmMatchKeysForPackages(sscc.packages, sscc.ssccId));

    const matches = await getReceiveManifestQmMatchesRequest(keys);
    if (refreshSeq !== qmMatchRefreshSeqRef.current) return;
    applyQmMatchMap(buildQmMatchMap(matches), 'replace');
  }

  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);

  useEffect(() => {
    qmMatchesByLineKeyRef.current = qmMatchesByLineKey;
  }, [qmMatchesByLineKey]);

  useEffect(() => {
    async function getUser() {
      const userDetails = await store.userDetails.get(user.cognitoUser.username, user.cognitoUser);
      if (userDetails) {
        setUserDetails(userDetails);
      }
    }
    getUser();
  }, [store.userDetails, user.cognitoUser]);

  useEffect(() => {
    async function getData() {
      try {
        if (!tripsheet) return;
        setTripData(tripsheet);
        const foundManifest = tripsheet.legs.flatMap((leg) => leg.manifests).find((m) => m.id === id);

        if (!foundManifest) {
          setManifest(undefined);
          setOriginalManifest(undefined);
          initialPostgresStatusLoadedManifestIdRef.current = null;
          setIsInitialPostgresStatusLoaded(true);
          return;
        }

        setIsInitialPostgresStatusLoaded(initialPostgresStatusLoadedManifestIdRef.current === foundManifest.id);

        const mergedManifest = JSON.parse(JSON.stringify(foundManifest)) as ManifestProps;

        mergeExistingLinesWithSubscription(mergedManifest);

        const syncedManifest = normalizeManifestPackageStatuses(mergedManifest);
        setManifest(syncedManifest);

        if (!originalManifest) {
          setOriginalManifest(JSON.parse(JSON.stringify(syncedManifest)));
        }
      } catch (error) {
        console.error(error);
        captureError(error, 'receiveManifestDetails-getData');
        alert('Failed to get TripSheet data', 'error');
      } finally {
        setIsLoading(false);
      }
    }

    if (!id) {
      setIsLoading(false);
      setIsInitialPostgresStatusLoaded(true);
      alert('Manifest ID is missing, please go back to the list and try again!', 'error');
      return;
    }

    if (!tripId) {
      setIsLoading(false);
      setIsInitialPostgresStatusLoaded(true);
      alert('Trip ID is missing, please go back to the list and try again!', 'error');
      return;
    }

    getData();
    // DataStore subscription merge must compare against the current manifest baseline without refiring for every local edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripsheet]);

  useEffect(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const searchMatchedSsccs = (manifest?.ssccs || []).filter((sscc) => {
      if (normalizedSearch.length === 0) {
        return true;
      }

      return sscc.packages.some((pkg) =>
        String(pkg.asn || '')
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
    const nextItemStatusFilterCounts = searchMatchedSsccs.reduce<Record<string, number>>(
      (counts, sscc) => {
        for (const pkg of sscc.packages || []) {
          const status = getPackageItemStatusFilterValue(pkg, sscc);
          counts[ITEM_STATUS_FILTER_ALL] += 1;
          counts[status] = (counts[status] || 0) + 1;
        }

        return counts;
      },
      { [ITEM_STATUS_FILTER_ALL]: 0 }
    );
    const filteredResults = searchMatchedSsccs
      .map((sscc) => {
        if (itemStatusFilter === ITEM_STATUS_FILTER_ALL) {
          return sscc;
        }

        const filteredPackages = sscc.packages.filter(
          (pkg) => getPackageItemStatusFilterValue(pkg, sscc) === itemStatusFilter
        );

        return filteredPackages.length > 0 ? { ...sscc, packages: filteredPackages } : null;
      })
      .filter((sscc): sscc is ManifestSSCCProps => Boolean(sscc));

    setItemStatusFilterCounts(nextItemStatusFilterCounts);
    setFilteredSsccs(filteredResults);
    // getPackageItemStatusFilterValue is a local status formatter; these are the state inputs that change visible row status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, manifest, itemStatusFilter, qmMatchesByLineKey, partialStatusHistoryByKey]);

  useEffect(() => {
    if (!manifest) {
      return;
    }

    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return;
    }

    const matchingSsccIds = manifest.ssccs
      .filter((sscc) =>
        sscc.packages.some((pkg) =>
          String(pkg.asn || '')
            .toLowerCase()
            .includes(normalizedSearch)
        )
      )
      .map((sscc) => sscc.ssccId);

    if (matchingSsccIds.length === 0) {
      return;
    }

    setExpandedSsccs((prev) => {
      const next = { ...prev };
      matchingSsccIds.forEach((ssccId) => {
        next[ssccId] = true;
      });
      return next;
    });
  }, [search, manifest]);

  useEffect(() => {
    async function loadPartialStatusHistory() {
      if (!manifest?.id) {
        setPartialStatusHistoryByKey({});
        setIsInitialPostgresStatusLoaded(true);
        return;
      }

      try {
        setIsPartialStatusLoading(true);
        await refreshPartialStatusHistoryForManifest(manifest.id, true);
      } catch (error) {
        captureError(error, 'receiveManifestV2-loadPartialStatusHistory');
      } finally {
        setIsPartialStatusLoading(false);
        initialPostgresStatusLoadedManifestIdRef.current = manifest.id;
        setIsInitialPostgresStatusLoaded(true);
      }
    }

    void loadPartialStatusHistory();
    // Initial history load is keyed by manifest identity; local quantity edits should not refetch history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.id]);

  useEffect(() => {
    if (!manifest?.id) return undefined;

    return subscribeManifestPartialReceiptUpdates(manifest.id, (result) => {
      const latestLines = result.lines || [];
      setPartialStatusHistoryByKey(buildPartialStatusHistoryMap(latestLines));
      setManifest((prev) => {
        if (!prev || prev.id !== result.manifestId) {
          return prev;
        }

        return applyPartialReceiptQuantitiesToManifest(prev, latestLines);
      });
    });
    // Partial receipt live updates only need to resubscribe when the manifest identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.id]);

  useEffect(() => {
    async function loadQmMatchesForManifestLines() {
      try {
        await refreshQmMatchesForManifestLines(manifest);
      } catch (error) {
        captureError(error, 'receiveManifestV2-loadQmMatchesForManifestLines');
      }
    }

    void loadQmMatchesForManifestLines();
    // Initial QM match load is keyed by manifest identity; explicit refresh calls handle submit-time freshness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.id]);

  useEffect(() => {
    if (!manifest?.id) return undefined;

    const handleSameTabSiteInboundQmCreated = (event: Event) => {
      const detail = (event as CustomEvent<{ manifestId?: string | null }>).detail;
      const currentManifest = manifestRef.current;
      if (!currentManifest?.id) return;

      const eventManifestId = String(detail?.manifestId || '').trim();
      if (!eventManifestId || eventManifestId !== String(currentManifest.id)) return;

      void refreshQmMatchesForManifestLines(currentManifest).catch((error) => {
        captureError(error, 'receiveManifestV2-refreshQmMatchesOnSameTabCreate');
      });
    };

    window.addEventListener('ftl:site-inbound-qm-created', handleSameTabSiteInboundQmCreated);

    const unsubscribe = subscribeQmLiveUpdates((event) => {
      const record = event.record;
      if (record.type !== 'SITE_INBOUND') return;

      const currentManifest = manifestRef.current;
      if (!currentManifest?.id) return;

      const eventManifestId = String(record.manifestId || '').trim();
      if (!eventManifestId || eventManifestId !== String(currentManifest.id)) return;

      void refreshQmMatchesForManifestLines(currentManifest).catch((error) => {
        captureError(error, 'receiveManifestV2-refreshQmMatchesOnLiveUpdate');
      });
    });

    return () => {
      window.removeEventListener('ftl:site-inbound-qm-created', handleSameTabSiteInboundQmCreated);
      unsubscribe();
    };
    // QM live updates only need to resubscribe when the manifest identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest?.id]);

  useEffect(() => {
    isUiSectionStateHydratedRef.current = false;
    skipNextUiSectionPersistRef.current = true;

    if (!id) {
      setExpandedSsccs({});
      setExpandedAsnGroups({});
      isUiSectionStateHydratedRef.current = true;
      return;
    }

    try {
      const rawState = localStorage.getItem(`receiveManifestV2:${id}:uiSections`);
      if (!rawState) {
        setExpandedSsccs({});
        setExpandedAsnGroups({});
      } else {
        const parsedState = JSON.parse(rawState) as {
          expandedSsccs?: Record<string, boolean>;
          expandedAsnGroups?: Record<string, boolean>;
        };

        setExpandedSsccs(parsedState.expandedSsccs || {});
        setExpandedAsnGroups(parsedState.expandedAsnGroups || {});
      }
    } catch {
      setExpandedSsccs({});
      setExpandedAsnGroups({});
    } finally {
      isUiSectionStateHydratedRef.current = true;
    }
  }, [id]);

  useEffect(() => {
    if (!id || !isUiSectionStateHydratedRef.current) {
      return;
    }

    if (skipNextUiSectionPersistRef.current) {
      skipNextUiSectionPersistRef.current = false;
      return;
    }

    try {
      localStorage.setItem(
        `receiveManifestV2:${id}:uiSections`,
        JSON.stringify({
          expandedSsccs,
          expandedAsnGroups
        })
      );
    } catch {
      // Ignore storage write failures and keep UI functional.
    }
  }, [id, expandedSsccs, expandedAsnGroups]);

  useEffect(() => {
    if (!manifest?.id || !ssccIdsSignature) {
      return;
    }

    const manifestExpansionKey = `${manifest.id}:${ssccIdsSignature}`;
    if (autoExpandedSsccManifestKeyRef.current === manifestExpansionKey) {
      return;
    }

    autoExpandedSsccManifestKeyRef.current = manifestExpansionKey;
    setExpandedSsccs(Object.fromEntries(manifest.ssccs.map((sscc) => [sscc.ssccId, true])));
  }, [manifest?.id, manifest?.ssccs, ssccIdsSignature]);

  function updateSsccLine(sourceManifest = manifest): ManifestProps | undefined {
    if (!sourceManifest || sourceManifest.status === ManifestStatus.completed) {
      return sourceManifest;
    }

    const updatedSsccs = sourceManifest.ssccs.map((sscc) => {
      const nonVariousPackages = sscc.packages.filter((pkg) => pkg.asn !== variousAsn);

      const isComplete = nonVariousPackages.every(
        (pkg) =>
          pkg.receivedStatus === ManifestPackageStatus.received || pkg.receivedStatus === ManifestPackageStatus.direct
      );

      const isPartial = nonVariousPackages.some(
        (pkg) =>
          pkg.receivedStatus === ManifestPackageStatus.partial ||
          pkg.receivedStatus === ManifestPackageStatus.received ||
          pkg.receivedStatus === ManifestPackageStatus.qm_pending ||
          pkg.receivedStatus === ManifestPackageStatus.direct_pending
      );

      const updatedPackages = sscc.packages.map((pkg) => {
        if (pkg.asn === variousAsn) {
          return {
            ...pkg,
            receivedStatus: isComplete
              ? ManifestPackageStatus.received
              : isPartial
                ? ManifestPackageStatus.partial
                : ManifestPackageStatus.notReceived
          };
        }
        return pkg;
      });

      return { ...sscc, packages: updatedPackages };
    });

    const updatedManifest = { ...sourceManifest, ssccs: updatedSsccs };
    setManifest(updatedManifest);
    return updatedManifest;
  }

  const allLinesFilledForSubmit = manifest ? isManifestFullyReadyForCompletion(manifest) : false;
  const hasOpenQmForSubmit = hasOpenQmInManifest(manifest);
  const allLinesReadyForSubmit = allLinesFilledForSubmit && !hasOpenQmForSubmit;
  const selectedPartialSubmitPackages = manifest ? getSelectedPackagesForPartialSubmit(manifest) : [];
  const partialSelectedCount = selectedPartialSubmitPackages.length;
  const partialSelectedReceivableLineCount = selectedPartialSubmitPackages.reduce(
    (count, pkg) => count + getSelectedUnblockedPartialLines(pkg).length,
    0
  );
  const isPartialSubmitMode = partialSelectedCount > 0 || !allLinesReadyForSubmit;
  const canSubmit = Boolean(manifest && (allLinesReadyForSubmit || partialSelectedReceivableLineCount > 0));
  const hasManifestLineChanges = useMemo(() => {
    if (!originalManifest || !manifest) {
      return false;
    }

    const currentPackagesById = new Map(
      manifest.ssccs.flatMap((sscc) => sscc.packages).map((pkg) => [pkg.id, pkg.quantity])
    );

    for (const sscc of originalManifest.ssccs) {
      for (const pkg of sscc.packages) {
        if (currentPackagesById.get(pkg.id) !== pkg.quantity) {
          return true;
        }
      }
    }

    return false;
  }, [manifest, originalManifest]);
  const canSaveToPostgres = Boolean(
    manifest &&
    manifest.status === ManifestStatus.open &&
    !user.isReadOnly &&
    (qmDictionary.length > 0 || directDictionary.length > 0 || hasManifestLineChanges)
  );

  useEffect(() => {
    if (allLinesReadyForSubmit) {
      setSelectedPartialLineKeys({});
    }
  }, [allLinesReadyForSubmit]);

  function buildReceiptLineResultsForPackages(
    sourceManifest: ManifestProps,
    packages: ManifestPackageProps[],
    status: string,
    processedQuantityFactory: (quantity: number) => number,
    errorCode?: string,
    errorMessage?: string
  ): ManifestReceiptLineResultInput[] {
    const lineResults: ManifestReceiptLineResultInput[] = [];

    for (const pkg of packages) {
      const parentSsccId =
        sourceManifest.ssccs.find((sscc) => sscc.packages.some((manifestPkg) => manifestPkg.id === pkg.id))?.ssccId ||
        '';

      const singleManifest: ManifestProps = {
        ...sourceManifest,
        ssccs: [
          {
            ssccId: parentSsccId,
            packages: [pkg],
            totalWeight: 0
          }
        ]
      };

      const singleReceiptedItem = mapToReceiptedItems(
        singleManifest,
        buildCalculatedAsnLineReceiveQuantityOverrides(singleManifest)
      )[0];
      const receiptItems = singleReceiptedItem?.items || [];
      if (!singleReceiptedItem || receiptItems.length === 0) {
        continue;
      }

      receiptItems.forEach((item) => {
        const receiveQuantity = Number(item.quantityReceived || 0);
        lineResults.push({
          asn: pkg.asn,
          poLineId: String(item.orderLineId),
          itemId: String(item.partId),
          sourcePartId: String(item.sourcePartId || ''),
          packageId: pkg.id,
          selectedForReceive: true,
          requestedQuantity: receiveQuantity,
          receiveQuantity,
          processedQuantity: processedQuantityFactory(receiveQuantity),
          status,
          errorCode,
          errorMessage
        });
      });
    }

    return lineResults;
  }

  async function onSubmitManifest() {
    if (isManifestInteractionLocked || isSubmitting || isSaving) {
      return;
    }

    if (!manifest) {
      alert('Cannot load the manifest. Please reload the page.', 'error');
      return;
    }

    if (manifest.status === ManifestStatus.completed) {
      alert('Manifest has already been submitted.', 'warning');
      return;
    }

    // If offline, queue the request
    if (!isOnline) {
      const requestData = {
        manifest: JSON.parse(JSON.stringify(manifest)),
        tripData: JSON.parse(JSON.stringify(tripData)),
        userDetails: JSON.parse(JSON.stringify(userDetails))
      };

      queueOfflineRequest(manifest.id, user.cognitoUser.username, requestData);
      alert('Manifest submission has been queued and will be processed when you come back online.', 'info');
      return;
    }

    // Register the request as pending
    const requestId = registerPendingRequest(manifest.id, user.cognitoUser.username);
    submitRequestIdRef.current = requestId;

    try {
      setIsSubmitting(true);
      updateRequestStatus(requestId, 'PROCESSING');

      const selectedPackages = isPartialSubmitMode ? getSelectedPackagesForPartialSubmit(manifest) : [];
      const isPartialSelectionSubmit = selectedPackages.length > 0;

      let receiptedItems: Partial<ReceiptEventPackageProps>[] = [];
      let submitScopeManifest: ManifestProps | null = null;

      if (isPartialSelectionSubmit) {
        const selectedManifest = buildSelectedPartialSubmitManifest(manifest, selectedPackages);

        receiptedItems = keepPositiveReceiptQuantities(
          mapToReceiptedItems(
            selectedManifest,
            buildCalculatedAsnLineReceiveQuantityOverrides(selectedManifest, { excludeOpenQmLines: true }),
            { includeZeroQuantityPackages: true }
          )
        );
      } else {
        submitScopeManifest = buildSubmitScopeManifest(manifest);
        receiptedItems = mapToReceiptedItems(
          submitScopeManifest,
          buildCalculatedAsnLineReceiveQuantityOverrides(submitScopeManifest)
        );
      }

      if (receiptedItems.length === 0) {
        if (isPartialSelectionSubmit) {
          updateRequestStatus(requestId, 'FAILED');
          alert('No selected lines to submit. Enter quantities for the lines you want to submit first.', 'warning');
          return;
        }
      }

      if (isPartialSelectionSubmit) {
        if (!tripData) {
          updateRequestStatus(requestId, 'FAILED');
          alert('Cannot load trip data. Please reload the page before submitting selected lines.', 'error');
          return;
        }

        const persistResult = await persistReceiveManifestSnapshot({
          tripData,
          manifest,
          triggeredBy: userEmail,
          mode: 'SAVE',
          clientRequestId: `${requestId}:snapshot`,
          selectedPartialLineKeys
        });

        if (persistResult.error) {
          updateRequestStatus(requestId, 'FAILED');
          throw new Error(persistResult.error);
        }

        setOriginalManifest(JSON.parse(JSON.stringify(manifest)));
        setIsDirty(qmDictionary.length > 0 || directDictionary.length > 0);

        const blockingReason = await findBlockingQmForPartialSubmit(selectedPackages);
        if (blockingReason) {
          updateRequestStatus(requestId, 'FAILED');
          alert(blockingReason, 'warning');
          return;
        }
      }

      alert(isPartialSelectionSubmit ? 'Submitting selected lines to Coupa' : 'Submitting manifest to Coupa', 'info');

      if (isPartialSelectionSubmit) {
        const lineResults: ManifestReceiptLineResultInput[] = [];

        for (const pkg of selectedPackages) {
          const parentSsccId =
            manifest.ssccs.find((sscc) => sscc.packages.some((manifestPkg) => manifestPkg.id === pkg.id))?.ssccId || '';

          const singleManifest: ManifestProps = {
            ...manifest,
            ssccs: [
              {
                ssccId: parentSsccId,
                packages: [pkg],
                totalWeight: 0
              }
            ]
          };
          const selectedSingleManifest = buildSelectedPartialSubmitManifest(singleManifest, [pkg]);

          const singleReceiptedItem = keepPositiveReceiptQuantities(
            mapToReceiptedItems(
              selectedSingleManifest,
              buildCalculatedAsnLineReceiveQuantityOverrides(selectedSingleManifest, { excludeOpenQmLines: true }),
              { includeZeroQuantityPackages: true }
            )
          )[0];
          const receiptItems = singleReceiptedItem?.items || [];
          if (!singleReceiptedItem || receiptItems.length === 0) {
            continue;
          }

          const buildLineResults = (
            status: string,
            processedQuantityFactory: (quantity: number) => number,
            errorCode?: string,
            errorMessage?: string
          ) => {
            receiptItems.forEach((item) => {
              const receiveQuantity = Number(item.quantityReceived || 0);
              lineResults.push({
                asn: pkg.asn,
                poLineId: String(item.orderLineId),
                itemId: String(item.partId),
                sourcePartId: String(item.sourcePartId || ''),
                packageId: pkg.id,
                selectedForReceive: true,
                requestedQuantity: receiveQuantity,
                receiveQuantity,
                processedQuantity: processedQuantityFactory(receiveQuantity),
                status,
                errorCode,
                errorMessage
              });
            });
          };

          try {
            const receiptResponse = await createSiteReceiptCoupa(singleReceiptedItem);
            if (receiptResponse.error) {
              buildLineResults('FAILED', () => 0, 'COUPA_RECEIPT_ERROR', receiptResponse.error);

              captureError(
                new Error(receiptResponse.error),
                'receiveManifestDetails-onSubmitManifest-createSiteReceiptCoupa'
              );
            } else {
              buildLineResults('RECEIVED_SUBMITTED', (receiveQuantity) => receiveQuantity);
            }
          } catch (error) {
            captureError(error, 'receiveManifestDetails-onSubmitManifest-createSiteReceiptCoupaException');
            buildLineResults(
              'FAILED',
              () => 0,
              'COUPA_RECEIPT_EXCEPTION',
              error instanceof Error ? error.message : 'Unknown error'
            );
          }
        }

        if (lineResults.length > 0) {
          const partialResult = await saveManifestPartialReceiptRequest({
            manifestId: manifest.id,
            tripId: tripId || null,
            legId: currentLegId,
            triggeredBy: userEmail,
            clientRequestId: requestId,
            lines: lineResults
          });

          setPartialStatusHistoryByKey(buildPartialStatusHistoryMap(partialResult.lines || []));
          setSelectedPartialLineKeys((prev) => {
            const next = { ...prev };
            for (const pkg of selectedPackages) {
              delete next[getPartialRowKey(pkg)];
              for (const line of pkg.inventoryLines || []) {
                delete next[getPartialAsnLineKey(pkg.asn, line)];
              }
            }
            return next;
          });

          const failedCount = (partialResult.lines || []).filter((line) => line.status === 'FAILED').length;
          try {
            await refreshQmMatchesForManifestLines(manifest);
          } catch (error) {
            captureError(error, 'receiveManifestV2-refreshQmMatchesAfterPartialSubmit');
          }

          if (failedCount > 0) {
            alert('Partial submit complete. Some lines failed and can be reviewed in row history.', 'warning');
          } else {
            alert('Partial submit complete.', 'success');
          }
        }

        updateRequestStatus(requestId, 'COMPLETED');
        return;
      }

      for (const receiptedItem of receiptedItems) {
        const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
        if (receiptResponse.error) {
          updateRequestStatus(requestId, 'FAILED');
          alert('Error receipting items to coupa, please try again.', 'error');
          console.error(receiptResponse.error);
          captureError(
            new Error(receiptResponse.error),
            'receiveManifestDetails-onSubmitManifest-createSiteReceiptCoupa'
          );
          return;
        }
      }

      const submitScopePackages = submitScopeManifest?.ssccs.flatMap((sscc) => sscc.packages) || [];
      const submitLineResults = submitScopeManifest
        ? buildReceiptLineResultsForPackages(
            manifest,
            submitScopePackages,
            'RECEIVED_SUBMITTED',
            (receiveQuantity) => receiveQuantity
          )
        : [];

      if (submitLineResults.length > 0) {
        const submitPartialResult = await saveManifestPartialReceiptRequest({
          manifestId: manifest.id,
          tripId: tripId || null,
          legId: currentLegId,
          triggeredBy: userEmail,
          clientRequestId: `${requestId}:submit-results`,
          lines: submitLineResults
        });
        setPartialStatusHistoryByKey(buildPartialStatusHistoryMap(submitPartialResult.lines || []));
      }

      const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

      const event: UpdateAsnProps = {
        asns: uniqueAsns.filter((asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn)
      };

      if (event.asns.length > 0) {
        await updateManifestAsnsCoupa(event);
      }

      const submitManifest = {
        ...manifest,
        status: ManifestStatus.completed
      };
      setManifest(submitManifest);
      alert('Saving changes', 'info');
      await onSaveChangesWithoutSnapshot(submitManifest);

      if (isOnline && tripData) {
        fireAndForgetManifestSnapshotPersist(
          tripData,
          submitManifest,
          'SUBMIT',
          'receiveManifestDetails-onSubmitPersist',
          undefined,
          `${requestId}:final`
        );
      }

      await handleSsccAsnStatusUpdate(tripData);

      // Mark request as completed
      updateRequestStatus(requestId, 'COMPLETED');
      alert('Manifest submitted successfully', 'success');
    } catch (error) {
      console.error('Error submitting manifest: ', error);
      captureError(error, 'receiveManifestDetails-onSubmitManifest');
      updateRequestStatus(requestId, 'FAILED');
      alert('Error submitting manifest. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
      submitRequestIdRef.current = null;
    }
  }

  async function onSaveTableToPostgres() {
    if (isManifestInteractionLocked) {
      return;
    }

    if (!manifest) {
      alert('Cannot load the manifest. Please reload the page.', 'error');
      return;
    }

    if (!tripData) {
      alert('Cannot load trip data. Please reload the page.', 'error');
      return;
    }

    if (!isOnline) {
      alert('You are offline. Must be online to save the table.', 'warning');
      return;
    }

    try {
      setIsSaving(true);
      const persistResult = await persistReceiveManifestSnapshot({
        tripData,
        manifest,
        triggeredBy: userEmail,
        mode: 'SAVE',
        clientRequestId: crypto.randomUUID(),
        selectedPartialLineKeys
      });

      if (persistResult.error) {
        throw new Error(persistResult.error);
      }

      let historyRefreshFailed = false;
      try {
        await refreshPartialStatusHistoryForManifest(manifest.id, false);
      } catch (error) {
        historyRefreshFailed = true;
        captureError(error, 'receiveManifestDetails-refreshHistoryAfterSave');
      }

      // Treat a successful save as the new baseline for quantity-change dirty detection.
      setOriginalManifest(JSON.parse(JSON.stringify(manifest)));
      setIsDirty(qmDictionary.length > 0 || directDictionary.length > 0);
      alert(
        historyRefreshFailed
          ? 'Current table data saved, but history could not refresh. Reload the page if history is not visible.'
          : 'Current table data saved.',
        historyRefreshFailed ? 'warning' : 'success'
      );
    } catch (error) {
      captureError(error, 'receiveManifestDetails-onSaveTableToPostgres');
      alert('Failed to save table data.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSsccAsnStatusUpdate(tripsheet: TripSheetProps | undefined) {
    if (!tripsheet) return;
    for (const leg of tripsheet.legs) {
      for (const manifest of leg.manifests) {
        for (const sscc of manifest.ssccs) {
          const foundSscc = await store.ssccs.get(sscc.ssccId);
          if (!foundSscc) {
            console.info(`SSCC with an ID ${sscc.ssccId} not found - ignoring asn update`);
            continue;
          }
          updateAsnStatus(foundSscc, 'Available');
          await store.ssccs.update(foundSscc);
        }
      }
    }
  }

  function mergeExistingLinesWithSubscription(incomingManifest: ManifestProps) {
    for (let incomingSscc of incomingManifest.ssccs) {
      updateLines(incomingSscc);
    }
  }

  function updateLines(incomingSscc: ManifestSSCCProps) {
    for (let currentSscc of manifest?.ssccs || []) {
      if (currentSscc.ssccId === incomingSscc.ssccId) {
        incomingSscc.packages.forEach((incomingPackage) => {
          const currentPackage = currentSscc.packages.find((p) => p.id === incomingPackage.id);
          if (currentPackage) {
            if (
              incomingPackage.receivedStatus === ManifestPackageStatus.direct ||
              incomingPackage.receivedStatus === ManifestPackageStatus.qm
            ) {
              incomingPackage.quantity = 0;
              return;
            }
            const isIncomingHigher = incomingPackage.quantity > currentPackage.quantity;
            incomingPackage.quantity = isIncomingHigher ? incomingPackage.quantity : currentPackage.quantity;
          }
        });
      }
    }
  }

  function fireAndForgetManifestSnapshotPersist(
    snapshotTripData: TripSheetProps,
    snapshotManifest: ManifestProps,
    mode: 'SAVE' | 'SUBMIT',
    sentryContext: string,
    selectedLineKeys?: Record<string, boolean>,
    clientRequestId?: string
  ) {
    void persistReceiveManifestSnapshot({
      tripData: snapshotTripData,
      manifest: snapshotManifest,
      triggeredBy: userEmail,
      mode,
      clientRequestId: clientRequestId || crypto.randomUUID(),
      selectedPartialLineKeys: selectedLineKeys
    })
      .then((persistResult) => {
        if (persistResult.error) {
          captureError(new Error(persistResult.error), sentryContext);
        }
      })
      .catch((error) => {
        captureError(error, sentryContext);
      });
  }

  async function onSaveChangesWithoutSnapshot(manifestOverride?: ManifestProps) {
    await onSaveChangesCore(false, manifestOverride);
  }

  async function onSaveChangesCore(shouldPersistSnapshot: boolean, manifestOverride?: ManifestProps) {
    if (!tripData) return;

    const sourceManifest = manifestOverride || manifest;
    if (!sourceManifest) return;

    try {
      setIsSaving(true);

      let updatedManifest: ManifestProps = JSON.parse(JSON.stringify(sourceManifest));
      if ((qmDictionary.length || directDictionary.length) > 0 && !isOnline) {
        alert('You are offline. Must be online to save Pending QM or Pending Direct', 'warning');
        return;
      }

      updatedManifest = await saveQMCoupa(updatedManifest);
      updatedManifest = await saveDirectReceipt(updatedManifest);
      updatedManifest = normalizeManifestPackageStatuses(updatedManifest);

      setOriginalManifest(JSON.parse(JSON.stringify(updatedManifest)));
      setManifest(updatedManifest);

      const updatedLegs = tripData.legs.map((leg) => {
        const manifestIndex = leg.manifests.findIndex((m) => m.id === updatedManifest.id);
        if (manifestIndex === -1) {
          return leg;
        }

        const nextManifests = [...leg.manifests];
        nextManifests[manifestIndex] = updatedManifest;

        return {
          ...leg,
          manifests: nextManifests
        };
      });

      const updatedTripSheet = {
        ...tripData,
        legs: updatedLegs
      };

      await store.tripSheets.update(updatedTripSheet);

      if (shouldPersistSnapshot && isOnline) {
        fireAndForgetManifestSnapshotPersist(
          updatedTripSheet,
          updatedManifest,
          'SAVE',
          'receiveManifestDetails-onSavePersist',
          selectedPartialLineKeys
        );
      }

      alert('Changes saved successfully', 'success');
      setDirectDictionary([]);
      setQMDictionary([]);
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      captureError(error, 'receiveManifestDetails-onSaveChanges');
      alert('Failed to save changes', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function saveQMCoupa(manifest: ManifestProps) {
    for (const qm of qmDictionary) {
      const qmResponse = await createQMCoupa(qm.value);
      if (qmResponse.error) {
        alert('Error creating QM in coupa, please try again.', 'error');
        console.error(qmResponse.error);
        captureError(new Error(qmResponse.error), 'receiveManifestDetails-saveQMCoupa-createQMCoupa');
        return manifest;
      }
      const modifiedKey = qm.key.split('_')[1];
      manifest.ssccs?.forEach((sscc) => {
        if (sscc.packages.some((pkg) => pkg.asn === modifiedKey)) {
          const packagesToUpdate = sscc.packages.filter(
            (pkg) => pkg.asn === modifiedKey || isChildLineWithoutQuantityInput(pkg)
          );
          packagesToUpdate.forEach((pkg) => {
            pkg.receivedStatus = ManifestPackageStatus.qm;
          });
        }
      });
    }
    setQMDictionary([]);
    return manifest;
  }

  async function saveDirectReceipt(manifest: ManifestProps) {
    for (const directReceipt of directDictionary) {
      const asn = `MRL${crypto.randomUUID().substring(0, 8)}`;
      directReceipt.value.asn = asn;

      const coupaReceiptEvent = convertReceiptEventToCoupaReceiptEvent(
        directReceipt.value,
        user.name,
        userDetails?.location?.name,
        asn,
        true
      );

      const receiptResponse = await createReceiptCoupa(coupaReceiptEvent);
      if (receiptResponse.error) {
        alert('Error receipting items to coupa, please try again.', 'error');
        console.error(receiptResponse.error);
        captureError(new Error(receiptResponse.error), 'receiveManifestDetails-saveDirectReceipt-createReceiptCoupa');
        return manifest;
      }
      const modifiedKey = directReceipt.key.split('_')[1];
      manifest.ssccs?.forEach((sscc) => {
        if (sscc.packages.some((pkg) => pkg.asn === modifiedKey)) {
          const packagesToUpdate = sscc.packages.filter(
            (pkg) => pkg.asn === modifiedKey || isChildLineWithoutQuantityInput(pkg)
          );
          packagesToUpdate.forEach((pkg) => {
            pkg.receivedStatus = ManifestPackageStatus.direct;
          });
        }
      });
    }
    setDirectDictionary([]);
    return manifest;
  }

  function onQuantityChange(id: string, quantity: string) {
    if (isManifestInteractionLocked) return;
    if (!manifest) return;

    const currentPackage = manifest.ssccs.flatMap((sscc) => sscc.packages).find((pkg) => pkg.id === id);
    if (!currentPackage || isPartialSuccess(currentPackage)) return;

    const parsedQuantity = Number.parseInt(quantity, 10);
    const safeQuantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;
    const updatedManifest: ManifestProps = {
      ...manifest,
      ssccs: manifest.ssccs.map((sscc) => ({
        ...sscc,
        packages: sscc.packages.map((pkg) => {
          if (pkg.id !== id) {
            return pkg;
          }

          const boundedQuantity = Math.max(0, Math.min(safeQuantity, Number(pkg.maxQuantity || 0)));
          const nextPackage = {
            ...pkg,
            quantity: boundedQuantity,
            receivedStatus:
              boundedQuantity === 0
                ? ManifestPackageStatus.notReceived
                : boundedQuantity > 0 && boundedQuantity < pkg.maxQuantity
                  ? ManifestPackageStatus.partial
                  : ManifestPackageStatus.received
          };
          return nextPackage;
        })
      }))
    };

    const changedPackage = updatedManifest.ssccs.flatMap((sscc) => sscc.packages).find((pkg) => pkg.id === id);
    if (!changedPackage) return;

    const manifestAfterSsccUpdate = updateSsccLine(updatedManifest) || updatedManifest;

    const changedSscc = manifestAfterSsccUpdate.ssccs.find((sscc) => sscc.packages.some((pkg) => pkg.id === id));
    if (changedSscc) {
      setSelectedPartialLineKeys((prev) => {
        const next = { ...prev };
        const changedSsccRowKeys = new Set(changedSscc.packages.map((pkg) => getPartialRowKey(pkg)));
        const eligibleRowKeys = new Set(
          changedSscc.packages
            .filter((pkg) => isPackageEligibleForPartialSubmit(pkg, manifestAfterSsccUpdate))
            .map((pkg) => getPartialRowKey(pkg))
        );

        for (const rowKey of changedSsccRowKeys) {
          delete next[rowKey];
        }

        for (const rowKey of eligibleRowKeys) {
          next[rowKey] = true;
        }

        return next;
      });
    }

    setIsDirty(checkIfManifestLinesChanged(updatedManifest));
  }

  async function updateQMStatus(data: RaiseQMNewModel) {
    try {
      if (isManifestInteractionLocked) {
        alert('Manifest data is still loading. Please try again in a moment.', 'info');
        return;
      }

      if (!manifest) {
        alert('Error updating manifest package, please try again.', 'warning');
        return;
      }

      const updatedManifest: ManifestProps = JSON.parse(JSON.stringify(manifest));
      const affectedPackages = getSelectedQmPackages(updatedManifest);

      if (!affectedPackages || affectedPackages.length === 0) {
        alert('Error updating manifest package, please try again.', 'warning');
        return;
      }

      if (!tripData) {
        alert('Cannot load trip data. Please reload the page before raising QM.', 'error');
        return;
      }

      if (!isOnline) {
        alert('You are offline. Must be online to raise Site Inbound QM.', 'warning');
        return;
      }

      const persistResult = await persistReceiveManifestSnapshot({
        tripData,
        manifest: updatedManifest,
        triggeredBy: userEmail,
        mode: 'SAVE',
        clientRequestId: crypto.randomUUID(),
        selectedPartialLineKeys
      });

      if (persistResult.error) {
        throw new Error(persistResult.error);
      }

      const submittedPackageConfigurations =
        data.packageConfigurations && data.packageConfigurations.length > 0
          ? data.packageConfigurations
          : Array.from(
              { length: Number(data.numberOfLabels || affectedPackages.length) || affectedPackages.length },
              (_, index) => ({
                packageNumber: index + 1,
                packageType: String(data.packageTypes || affectedPackages[index]?.packageType || '').trim()
              })
            );
      const selectedPoLines = (data.poLineSelections || []).map((line) => ({
        poLineId: String(line.poLineId || '').trim() || null,
        poLine: String(line.poLine || '').trim() || null,
        itemId: String(line.itemId || '').trim() || null,
        linePayload: line.linePayload || null,
        orderedQuantity: line.orderedQuantity,
        receivedQuantity: line.receivedQuantity,
        quantityVariance: line.quantityVariance
      }));
      const selectedReasonCode = String(data.reason.reasonId || data.reason.id || '').trim();
      const targetType = selectedQmScope === 'sscc' ? 'SSCC' : selectedPoLines.length > 0 ? 'ASN_LINE' : 'ASN';

      const baseCreateInput: CreateQmRecordInput = {
        type: 'SITE_INBOUND',
        reasonCode: selectedReasonCode,
        tripId: tripId || null,
        tripReference: tripReference || tripData?.reference || tripId || null,
        manifestId: manifest.id || id || null,
        targetType,
        sscc: selectedQmScope === 'sscc' ? selectedQmSsccId : null,
        asn: selectedQmScope === 'asn' ? selectedASN : null,
        poNumber: String(data.purchaseOrderNumber || affectedPackages[0]?.poNumber || '').trim() || null,
        poLine: selectedPoLines[0]?.poLine || null,
        poLineId:
          selectedPoLines[0]?.poLineId || String(affectedPackages[0]?.inventoryLines?.[0]?.orderLineId || '') || null,
        itemId: selectedPoLines[0]?.itemId || String(affectedPackages[0]?.inventoryLines?.[0]?.partId || '') || null,
        supplierCode: String(data.supplierCode || '').trim() || null,
        supplierName: String(data.supplierName || '').trim() || null,
        contactName: String(data.contactName || userEmail || '').trim() || null,
        receivingWarehouseId: data.receivingWarehouseId || selectedQmSiteId || null,
        itemLocation: String(data.locationName || '').trim(),
        itemLocationId: data.itemLocationId ?? null,
        packageCount: Number(data.numberOfLabels || affectedPackages.length) || affectedPackages.length,
        packageType: String(data.packageTypes || affectedPackages[0]?.packageType || '').trim() || null,
        otherInformation: String(data.other || '').trim() || null,
        description: String(data.description || '').trim() || null,
        packages: submittedPackageConfigurations.map((pkg, index) => ({
          packageNumber: Number(pkg.packageNumber || index + 1),
          packageType: String(pkg.packageType || data.packageTypes || '').trim() || null
        })),
        poLines: selectedPoLines,
        createdBy: userEmail
      };

      const createInputs: CreateQmRecordInput[] = [baseCreateInput];

      const createdRecords: Awaited<ReturnType<typeof createQmRecordRequest>>[] = [];
      for (const createInput of createInputs) {
        createdRecords.push(await createQmRecordRequest(createInput));
      }

      for (const createdRecord of createdRecords) {
        for (const attachment of data.attachments || []) {
          const file = await compressImageForUpload(attachment);
          const { uploadUrl, s3Key } = await getUploadUrlRequest({
            qmId: createdRecord.qmId,
            fileName: file.name,
            mimeType: file.type || null
          });
          await uploadFileToS3(uploadUrl, file);
          await confirmAttachmentRequest({
            qmId: createdRecord.qmId,
            s3Key,
            fileName: file.name,
            mimeType: file.type || null,
            sizeBytes: file.size,
            uploadedBy: userEmail
          });
        }
      }

      const shouldPrintLabels = data.printLabels !== false && createdRecords.length > 0;
      if (shouldPrintLabels) {
        const userPrinter = await store.printers.getPrinterForUser(user.cognitoUser.username);
        if (!userPrinter) {
          alert(
            `QM ${createdRecords.map((record) => record.qmNumber).join(', ')} created, but no printer is set for your account. Labels were not printed.`,
            'warning'
          );
        } else {
          for (const createdRecord of createdRecords) {
            const effectivePackageCount = Number(
              createdRecord.packageCount || data.numberOfLabels || affectedPackages.length || 0
            );
            if (effectivePackageCount <= 0) continue;
            const createdAt = new Date(createdRecord.createdAt);
            const formattedCreatedAt = Number.isNaN(createdAt.getTime())
              ? createdRecord.createdAt
              : createdAt.toLocaleDateString('en-AU');
            const printEvents: QMPrintProps[] = Array.from({ length: effectivePackageCount }, (_, index) => ({
              createdAt: formattedCreatedAt,
              createdBy: String(userEmail || user.name || '').trim(),
              packageNumber: String(index + 1),
              totalPackages: String(effectivePackageCount),
              qmNumber: String(createdRecord.qmNumber || ''),
              poNumber: String(
                createdRecord.poNumber || data.purchaseOrderNumber || affectedPackages[0]?.poNumber || ''
              ),
              reason: String(createdRecord.reasonLabel || data.reason?.name || '').trim(),
              issue: '-',
              locationName: String(data.itemLocationLabel || createdRecord.itemLocation || data.locationName || ''),
              description: String(createdRecord.description || data.description || '').trim()
            }));

            for (const printEvent of printEvents) {
              const printResponse = await printQm([printEvent], userPrinter.printerIP, userPrinter.printerPort);
              if (printResponse.error) {
                alert(
                  `QM ${createdRecord.qmNumber} created, but label ${printEvent.packageNumber} printing failed.`,
                  'warning'
                );
                break;
              }
            }
          }
        }
      }

      affectedPackages.forEach((pkg) => (pkg.receivedStatus = ManifestPackageStatus.qm));
      alert(`QM created: ${createdRecords.map((record) => record.qmNumber).join(', ')}`, 'success');
      updateSsccLine();
      await refreshQmMatchesForManifestLines(updatedManifest);
    } catch (error) {
      console.error("Error updating manifest package's QM status: ", error);
      captureError(error, 'receiveManifestDetails-updateQMStatus');
      alert('Error updating manifest package, please try again.', 'error');
    } finally {
      setOpenRaiseQM(false);
      setIsDirty(true);
    }
  }

  async function updateDirectStatus(pkg: ReceiptEventPackageProps) {
    if (isManifestInteractionLocked) {
      alert('Manifest data is still loading. Please try again in a moment.', 'info');
      setopenDirectModel(false);
      return;
    }

    const key = `direct_${selectedASN}`;
    setDirectDictionary((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.key === key);
      if (existingIndex < 0) {
        return [...prev, { key, value: pkg }];
      }

      return prev.map((entry, index) => (index === existingIndex ? { key, value: pkg } : entry));
    });

    if (!manifest) {
      alert('Error updating manifest package, please try again.', 'warning');
      setopenDirectModel(false);
      return;
    }

    const updatedManifest: ManifestProps = {
      ...manifest,
      ssccs: manifest.ssccs.map((sscc) => {
        const containsSelectedAsn = sscc.packages.some((pkg) => pkg.asn === selectedASN);
        if (!containsSelectedAsn) {
          return sscc;
        }

        return {
          ...sscc,
          packages: sscc.packages.map((pkg) =>
            pkg.asn === selectedASN || isChildLineWithoutQuantityInput(pkg)
              ? { ...pkg, receivedStatus: ManifestPackageStatus.direct }
              : pkg
          )
        };
      })
    };

    setManifest(updatedManifest);

    setopenDirectModel(false);
    setIsDirty(true);
    alert('Direct Receipt added. Save the table, then submit the manifest to complete the receipt update.', 'info');
  }

  function handleNavigateAway() {
    const hasPendingWorkflowChanges = qmDictionary.length > 0 || directDictionary.length > 0;
    const hasQuantityChanges = checkIfManifestLinesChanged();
    const isDirty = hasPendingWorkflowChanges || hasQuantityChanges;

    if (isDirty) {
      setOpenDirtyDialog(true);
    } else {
      navigateBackToReceiveManifestList();
    }
  }

  function navigateBackToReceiveManifestList() {
    navigate('/receive-manifest', { state: { preserveReceiveManifestFilters: true } });
  }

  function checkIfManifestLinesChanged(candidateManifest = manifest) {
    if (originalManifest && candidateManifest) {
      for (const sscc of originalManifest.ssccs) {
        for (const pkg of sscc.packages) {
          const correspondingPkg = candidateManifest.ssccs
            .flatMap((sscc) => sscc.packages)
            .find((p) => p.id === pkg.id);

          if (correspondingPkg && correspondingPkg.quantity !== pkg.quantity) {
            return true;
          }
        }
      }
    }
    return false;
  }

  function renderManifestHeaderInfo() {
    return (
      <>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" color={theme.palette.secondary.contrastText}>
            Site
          </Typography>
          <Typography
            color={theme.palette.primary.contrastText}
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {legDestination}
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="body2" color={theme.palette.secondary.contrastText}>
            Trip
          </Typography>
          <Typography color={theme.palette.primary.contrastText} sx={{ whiteSpace: 'nowrap' }}>
            {tripReference}
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="body2" color={theme.palette.secondary.contrastText}>
            Manifest
          </Typography>
          <Typography color={theme.palette.primary.contrastText} sx={{ whiteSpace: 'nowrap' }}>
            {manifest?.id}
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="body2" color={theme.palette.secondary.contrastText}>
            Status
          </Typography>
          <Typography color={theme.palette.primary.contrastText} sx={{ whiteSpace: 'nowrap' }}>
            {manifest?.status}
          </Typography>
        </Stack>
      </>
    );
  }

  function renderAsnLinesDrawer(lines: ManifestPackageInvLineItemProps[], asn?: string, sscc?: string | number | null) {
    const hasOpenLineQm = hasOpenQmOnAsnLines(asn, lines, sscc);

    return (
      <Box
        sx={{
          ml: { xs: 2, md: 8 },
          mr: 2,
          p: 0.9,
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: '8px',
          backgroundColor: theme.palette.action.hover
        }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, mb: 0.45 }}>
          ASN line details ({lines.length})
        </Typography>
        <Stack spacing={0.3}>
          {lines.map((line, index) => {
            const openLineQmMatches = asn ? getOpenQmMatchesForLine(asn, line, sscc) : [];
            const openLineQm = openLineQmMatches[0] || null;
            const linePackage = findPackageForAsnLine(asn, line);
            const isLinePartialSuccess = isAsnLinePartialSuccess(asn, line);
            const lineSelectionKey = asn ? getPartialAsnLineKey(asn, line) : '';
            const canSelectLinePartial = Boolean(
              asn &&
              linePackage &&
              hasOpenLineQm &&
              getRemainingAsnLineReceiveQuantity(asn, line) > 0 &&
              !openLineQm &&
              !isLinePartialSuccess &&
              !legStatusLock &&
              manifestIsOpen &&
              !user.isReadOnly
            );
            const isLineSelected = Boolean(lineSelectionKey && selectedPartialLineKeys[lineSelectionKey]);
            const displayedQuantity = getDefaultAsnLineReceiveQuantity(line);

            return (
              <Box
                key={`${line.orderLineId}-${line.partId}-${line.sourcePartId}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '78px 18px minmax(70px, auto) 1fr auto',
                    md: '86px 20px minmax(84px, auto) 1fr minmax(128px, auto)'
                  },
                  columnGap: { xs: 0.7, md: 0.9 },
                  alignItems: 'center',
                  py: 0.25,
                  px: 0.4,
                  borderRadius: '4px'
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {openLineQm ? (
                    renderQmCheckboxAreaChip(openLineQm, {
                      aggregateCount: openLineQmMatches.length,
                      aggregateMatches: openLineQmMatches
                    })
                  ) : isLinePartialSuccess ? (
                    <CheckCircleIcon fontSize="small" sx={partialSuccessIconSx} />
                  ) : canSelectLinePartial ? (
                    <Checkbox
                      size="small"
                      checked={isLineSelected}
                      onChange={(event) => asn && togglePartialAsnLineSelection(asn, line, event.target.checked)}
                      disabled={isManifestInteractionLocked}
                      sx={tableCheckboxSx}
                    />
                  ) : null}
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {index + 1}
                </Typography>
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: theme.palette.text.secondary }}>
                  {line.sourcePartId || line.partId}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    pr: 0.8,
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {line.description || 'No description'}
                </Typography>
                <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="flex-end">
                  <OutlinedInput
                    sx={{ ...quantityInputSx, width: '4.8em' }}
                    inputProps={{
                      style: { padding: '0.35em' },
                      min: 0,
                      max: getDefaultAsnLineReceiveQuantity(line),
                      step: 1
                    }}
                    type="number"
                    value={displayedQuantity}
                    disabled
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      whiteSpace: 'nowrap',
                      fontWeight: 600
                    }}>
                    of {getDefaultAsnLineReceiveQuantity(line)}
                  </Typography>
                  <Typography variant="caption" color={theme.palette.text.secondary} sx={{ whiteSpace: 'nowrap' }}>
                    {getAsnLineQuantityHelpText(asn, line)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  function shouldShowButtonsForTBF(pkg: ManifestPackageProps, sscc: ManifestSSCCProps) {
    return pkg.asn.startsWith(transportBookingFormPrefix) && pkg === sscc.packages[0];
  }

  function shouldShowButtonsForNonTBF(
    pkg: ManifestPackageProps,
    isFirstPackage: boolean,
    sameAsn: boolean,
    isPackageWithinTbf: boolean
  ) {
    return (
      isPackageWithinTbf &&
      !pkg.asn.startsWith(transportBookingFormPrefix) &&
      !isVarious(pkg) &&
      (!sameAsn || isFirstPackage)
    );
  }

  function isChildLineWithoutQuantityInput(pkg: ManifestPackageProps) {
    return pkg.asn === '1';
  }

  function isVarious(pkg: ManifestPackageProps) {
    return pkg.asn?.toUpperCase() === variousAsn;
  }

  function getOriginalPackageById(packageId: string): ManifestPackageProps | undefined {
    return originalManifest?.ssccs.flatMap((sscc) => sscc.packages).find((pkg) => pkg.id === packageId);
  }

  function isPackageChangedForSubmit(pkg: ManifestPackageProps): boolean {
    const originalPkg = getOriginalPackageById(pkg.id);
    if (!originalPkg) {
      return true;
    }

    return pkg.quantity !== originalPkg.quantity || pkg.receivedStatus !== originalPkg.receivedStatus;
  }

  function isPackageEligibleForPartialSubmit(pkg: ManifestPackageProps, sourceManifest = manifest): boolean {
    if (!sourceManifest) {
      return false;
    }

    let isFirstAsnRow = true;
    let hasMultipleRowsForSameAsn = false;
    let isSameAsnGroupFullyFilled = true;
    for (const sscc of sourceManifest.ssccs) {
      const packageIndex = sscc.packages.findIndex((currentPackage) => currentPackage.id === pkg.id);
      if (packageIndex === -1) {
        continue;
      }

      const firstAsnIndex = sscc.packages.findIndex((currentPackage) => currentPackage.asn === pkg.asn);
      isFirstAsnRow = firstAsnIndex === packageIndex;

      const normalizedAsn = String(pkg.asn || '')
        .trim()
        .toUpperCase();
      const sameAsnPackages = sscc.packages.filter(
        (currentPackage) =>
          String(currentPackage.asn || '')
            .trim()
            .toUpperCase() === normalizedAsn
      );
      hasMultipleRowsForSameAsn = normalizedAsn !== String(variousAsn).toUpperCase() && sameAsnPackages.length > 1;
      if (hasMultipleRowsForSameAsn) {
        isSameAsnGroupFullyFilled = sameAsnPackages.every(
          (currentPackage) => Number(currentPackage.quantity || 0) === Number(currentPackage.maxQuantity || 0)
        );
      }
      break;
    }

    if (!isFirstAsnRow) {
      return false;
    }

    const hasFtlAsn = pkg.asn.startsWith(ftlPrefix);
    const isTransportBookingPackage = pkg.asn.startsWith(transportBookingFormPrefix);
    const isFullyFilled = Number(pkg.quantity || 0) === Number(pkg.maxQuantity || 0);

    return (
      isPackageEligibleForCreateSiteReceipt(pkg, isTransportBookingPackage, hasFtlAsn) &&
      isFullyFilled &&
      !isPartialSuccess(pkg) &&
      (!hasMultipleRowsForSameAsn || isSameAsnGroupFullyFilled)
    );
  }

  function getSelectedPackagesForPartialSubmit(sourceManifest: ManifestProps): ManifestPackageProps[] {
    return sourceManifest.ssccs
      .flatMap((sscc) => sscc.packages)
      .filter(
        (pkg) =>
          (isPartialPackageRowSelected(pkg) || hasPartialLineSelectionForPackage(pkg)) &&
          (isPackageEligibleForPartialSubmit(pkg) || hasPartialLineSelectionForPackage(pkg))
      );
  }

  function getVisibleEligiblePackagesForPartialSelect(): ManifestPackageProps[] {
    return (filteredSsccs || [])
      .flatMap((sscc) => sscc.packages)
      .filter((pkg) => isPackageEligibleForPartialSubmit(pkg));
  }

  function toggleSsccExpanded(ssccId: string) {
    if (isManifestInteractionLocked) return;

    setExpandedSsccs((prev) => ({
      ...prev,
      [ssccId]: !prev[ssccId]
    }));
  }

  function toggleAsnGroupExpanded(ssccId: string, asn: string) {
    if (isManifestInteractionLocked) return;

    const groupKey = getAsnGroupKey(ssccId, asn);
    setExpandedAsnGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  }

  function toggleAllVisibleSsccs() {
    if (isManifestInteractionLocked) return;

    const visibleSsccIds = (filteredSsccs || []).map((sscc) => sscc.ssccId);
    const areAllOpen = visibleSsccIds.length > 0 && visibleSsccIds.every((ssccId) => Boolean(expandedSsccs[ssccId]));
    const collapsedVisibleSsccIds = visibleSsccIds.filter((ssccId) => !expandedSsccs[ssccId]);
    const ssccIdsToUpdate = areAllOpen ? visibleSsccIds : collapsedVisibleSsccIds.slice(0, MAX_EXPAND_ALL_SSCC_COUNT);

    setExpandedSsccs((prev) => {
      const next = { ...prev };
      ssccIdsToUpdate.forEach((ssccId) => {
        next[ssccId] = !areAllOpen;
      });
      return next;
    });
  }

  function toggleAllVisibleAsnGroups() {
    if (isManifestInteractionLocked) return;

    const visibleAsnGroupKeys = Array.from(
      new Set(
        (filteredSsccs || [])
          .flatMap((sscc) =>
            sscc.packages.filter((pkg) => isAsnGroupExpandable(pkg)).map((pkg) => getAsnGroupKey(sscc.ssccId, pkg.asn))
          )
          .filter(Boolean)
      )
    );

    const areAllOpen =
      visibleAsnGroupKeys.length > 0 && visibleAsnGroupKeys.every((groupKey) => Boolean(expandedAsnGroups[groupKey]));
    const collapsedVisibleAsnGroupKeys = visibleAsnGroupKeys.filter((groupKey) => !expandedAsnGroups[groupKey]);
    const asnGroupKeysToUpdate = areAllOpen
      ? visibleAsnGroupKeys
      : collapsedVisibleAsnGroupKeys.slice(0, MAX_EXPAND_ALL_ASN_GROUP_COUNT);

    setExpandedAsnGroups((prev) => {
      const next = { ...prev };
      asnGroupKeysToUpdate.forEach((groupKey) => {
        next[groupKey] = !areAllOpen;
      });
      return next;
    });
  }

  function toggleSelectAllVisiblePartialLines(checked: boolean) {
    if (isManifestInteractionLocked) return;

    const visibleEligiblePackages = getVisibleEligiblePackagesForPartialSelect();
    setSelectedPartialLineKeys((prev) => {
      const next = { ...prev };
      for (const pkg of visibleEligiblePackages) {
        const rowKey = getPartialRowKey(pkg);
        if (checked) {
          next[rowKey] = true;
        } else {
          delete next[rowKey];
        }
      }
      return next;
    });
  }

  function buildSubmitScopeManifest(sourceManifest: ManifestProps): ManifestProps {
    return {
      ...sourceManifest,
      ssccs: sourceManifest.ssccs
        .map((sscc) => ({
          ...sscc,
          packages: sscc.packages.filter(
            (pkg) =>
              isPackageChangedForSubmit(pkg) &&
              !isPartialSuccess(pkg) &&
              isPackageEligibleForCreateSiteReceipt(
                pkg,
                pkg.asn.startsWith(transportBookingFormPrefix),
                pkg.asn.startsWith(ftlPrefix)
              )
          )
        }))
        .filter((sscc) => sscc.packages.length > 0)
    };
  }

  function isManifestFullyReadyForCompletion(sourceManifest: ManifestProps): boolean {
    const packagesWithoutVarious = sourceManifest.ssccs
      .flatMap((sscc) => sscc.packages)
      .filter((pkg) => !isVarious(pkg));

    return packagesWithoutVarious.every((pkg) => {
      const hasFtlAsn = pkg.asn.startsWith(ftlPrefix);
      const isTransportBookingPackage = pkg.asn.startsWith(transportBookingFormPrefix);
      const quantityIsFull =
        !hasFtlAsn || isTransportBookingPackage || Number(pkg.quantity || 0) === Number(pkg.maxQuantity || 0);

      return (
        quantityIsFull &&
        (pkg.receivedStatus === ManifestPackageStatus.received ||
          pkg.receivedStatus === ManifestPackageStatus.direct ||
          pkg.receivedStatus === ManifestPackageStatus.direct_pending)
      );
    });
  }

  function renderDirectReceiptButton(pkg: ManifestPackageProps, disabled: boolean) {
    return isOnline ? (
      <Tooltip title="ASN was not found. Please manually search for the ASN and select the items." enterTouchDelay={0}>
        <LightInfoButton
          variant="contained"
          size="small"
          onClick={() => {
            setSelectedASN(pkg.asn);
            setopenDirectModel(true);
          }}
          disabled={isManifestInteractionLocked || disabled}
          sx={tableActionButtonSx}>
          Receipt
        </LightInfoButton>
      </Tooltip>
    ) : (
      <Tooltip enterTouchDelay={0} title="You are offline. This feature is not available when offline.">
        <LightInfoButton
          variant="outlined"
          size="small"
          disabled={isManifestInteractionLocked}
          sx={tableActionButtonSx}>
          Receipt
        </LightInfoButton>
      </Tooltip>
    );
  }

  const manifestIsOpen = manifest?.status === ManifestStatus.open;
  const legStatusLock = legStatus === ManifestLegStatus.inTransit;
  const isManifestInteractionLocked = isLoading || !isInitialPostgresStatusLoaded || isPartialStatusLoading;
  const visibleEligiblePackages = getVisibleEligiblePackagesForPartialSelect();
  const visibleEligibleRowKeys = Array.from(new Set(visibleEligiblePackages.map((pkg) => getPartialRowKey(pkg))));
  const visibleEligibleCount = visibleEligibleRowKeys.length;
  const visibleCheckedCount = visibleEligibleRowKeys.filter((rowKey) => selectedPartialLineKeys[rowKey]).length;
  const isSelectAllVisibleChecked =
    allLinesReadyForSubmit || (visibleEligibleCount > 0 && visibleCheckedCount === visibleEligibleCount);
  const isSelectAllVisibleIndeterminate =
    !allLinesReadyForSubmit && visibleCheckedCount > 0 && visibleCheckedCount < visibleEligibleCount;
  const visibleSsccIds = (filteredSsccs || []).map((sscc) => sscc.ssccId);
  const hasVisibleSsccs = visibleSsccIds.length > 0;
  const areAllVisibleSsccsOpen = hasVisibleSsccs && visibleSsccIds.every((ssccId) => Boolean(expandedSsccs[ssccId]));
  const visibleAsnGroupKeys = Array.from(
    new Set(
      (filteredSsccs || [])
        .flatMap((sscc) =>
          sscc.packages.filter((pkg) => isAsnGroupExpandable(pkg)).map((pkg) => getAsnGroupKey(sscc.ssccId, pkg.asn))
        )
        .filter(Boolean)
    )
  );
  const hasVisibleAsnGroups = visibleAsnGroupKeys.length > 0;
  const areAllVisibleAsnGroupsOpen =
    hasVisibleAsnGroups && visibleAsnGroupKeys.every((groupKey) => Boolean(expandedAsnGroups[groupKey]));
  const showCompactHeaderToolbar = !isPhoneLayout && isNarrowDesktop;
  const useStackedHeaderLayout = isMobile && !showCompactHeaderToolbar;
  const desktopHeaderHeight = isNarrowDesktop ? '7.9em' : '9.4em';
  const desktopHeaderPadding = isNarrowDesktop ? '0.5em 0' : '0.8em 0';
  const headerSearchWidth = isNarrowDesktop ? '15.5em' : '18.5em';
  const headerSearchMinWidth = isNarrowDesktop ? '16.5em' : '19.5em';
  const headerInfoSpacing = isNarrowDesktop ? 2.5 : 4;
  const leftRailWidth = showCompactHeaderToolbar ? '0px' : isNarrowDesktop ? '210px' : '250px';
  const itemStatusFilterOptionsWithCounts = ITEM_STATUS_FILTER_OPTIONS.map((option) => ({
    ...option,
    label: `${option.label} (${itemStatusFilterCounts[option.value] || 0})`
  }));
  const tableViewportHeight = isMobile
    ? 'calc(100dvh - 17em)'
    : isNarrowDesktop
      ? 'max(320px, calc(100dvh - 15em))'
      : 'max(360px, calc(100dvh - 16em))';
  const currentLegId =
    tripData?.legs.find((leg) => leg.manifests.some((currentManifest) => currentManifest.id === manifest?.id))?.id ||
    null;

  return (
    <>
      <DarkUpperSection>
        <Stack
          spacing={isNarrowDesktop ? 0.75 : 1.25}
          sx={{
            padding: isMobile ? '0.8em 0' : desktopHeaderPadding,
            height: isMobile ? 'auto' : desktopHeaderHeight,
            justifyContent: 'space-between',
            width: '100%'
          }}>
          <Stack
            direction={useStackedHeaderLayout ? 'column' : 'row'}
            spacing={useStackedHeaderLayout ? 0.75 : 3}
            sx={{ alignItems: useStackedHeaderLayout ? 'stretch' : 'center', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
              <IconButton onClick={handleNavigateAway}>
                <ArrowBackIcon fontSize="large" sx={{ color: theme.palette.primary.contrastText }} />
              </IconButton>
              <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
                Receive Manifest
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={headerInfoSpacing}
              sx={{
                alignItems: 'center',
                minWidth: 0,
                flex: 1,
                flexWrap: useStackedHeaderLayout ? 'wrap' : 'nowrap',
                overflow: 'hidden',
                pl: useStackedHeaderLayout ? 1 : 0
              }}>
              {renderManifestHeaderInfo()}
            </Stack>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: useStackedHeaderLayout ? '1fr' : 'auto auto minmax(0, 1fr)',
              columnGap: 1.25,
              rowGap: 1,
              alignItems: 'center',
              width: '100%'
            }}>
            <Stack sx={{ minWidth: useStackedHeaderLayout ? '100%' : headerSearchMinWidth, flexShrink: 0 }} spacing={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: useStackedHeaderLayout ? 'wrap' : 'nowrap',
                  rowGap: 1
                }}>
                <SearchField
                  placeholder="Scan Item"
                  startIcon={<SearchIcon />}
                  includeEndIcon
                  disabled={isManifestInteractionLocked}
                  onTrigger={(value) => setSearch(value)}
                  sx={{
                    width: useStackedHeaderLayout ? '100%' : headerSearchWidth,
                    maxWidth: useStackedHeaderLayout ? '20em' : 'none'
                  }}
                />
                <TextField
                  select
                  size="small"
                  value={itemStatusFilter}
                  onChange={(event) => setItemStatusFilter(event.target.value)}
                  disabled={isManifestInteractionLocked}
                  sx={{
                    ml: useStackedHeaderLayout ? 0 : 1,
                    width: useStackedHeaderLayout ? '100%' : '12.5em',
                    maxWidth: useStackedHeaderLayout ? '20em' : 'none',
                    flexShrink: 0,
                    backgroundColor: theme.palette.common.white,
                    borderRadius: 1,
                    '& .MuiInputBase-root': {
                      height: '45px',
                      fontSize: '0.82rem'
                    },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}>
                  {itemStatusFilterOptionsWithCounts.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1 }}>
                  {isPartialStatusLoading ? <CircularProgress size={16} /> : null}
                </Stack>
              </Box>
            </Stack>
            {showCompactHeaderToolbar ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexShrink: 0, justifySelf: useStackedHeaderLayout ? 'start' : 'start' }}>
                <StyledButton
                  variant="outlined"
                  size="small"
                  onClick={toggleAllVisibleSsccs}
                  disabled={isManifestInteractionLocked || !hasVisibleSsccs}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.68rem',
                    px: 0.8,
                    py: 0.3,
                    minWidth: '8.6em',
                    color: theme.palette.primary.contrastText,
                    borderColor: 'rgba(255,255,255,0.35)'
                  }}>
                  {areAllVisibleSsccsOpen ? 'Close All SSCC' : 'Expand All SSCC'}
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  size="small"
                  onClick={toggleAllVisibleAsnGroups}
                  disabled={isManifestInteractionLocked || !hasVisibleAsnGroups}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.68rem',
                    px: 0.8,
                    py: 0.3,
                    minWidth: '8.4em',
                    color: theme.palette.primary.contrastText,
                    borderColor: 'rgba(255,255,255,0.35)'
                  }}>
                  {areAllVisibleAsnGroupsOpen ? 'Close All ASN' : 'Expand All ASN'}
                </StyledButton>
              </Stack>
            ) : null}
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'flex-end',
                justifySelf: useStackedHeaderLayout ? 'stretch' : 'end',
                flexShrink: 0,
                width: useStackedHeaderLayout ? '100%' : 'auto'
              }}>
              <StyledButton
                variant="outlined"
                onClick={onSaveTableToPostgres}
                disabled={
                  isManifestInteractionLocked ||
                  !canSaveToPostgres ||
                  !isOnline ||
                  isSaving ||
                  isSubmitting ||
                  !manifestIsOpen ||
                  user.isReadOnly
                }
                sx={{ backgroundColor: theme.palette.common.white }}>
                Save
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={onSubmitManifest}
                disabled={
                  isManifestInteractionLocked ||
                  !canSubmit ||
                  !manifestIsOpen ||
                  user.isReadOnly ||
                  isSaving ||
                  isSubmitting
                }>
                {isPartialSubmitMode ? 'Submit Partial Manifest' : 'Submit Manifest'}
              </StyledButton>
            </Stack>
          </Box>
        </Stack>
      </DarkUpperSection>
      <PageSection
        sx={{
          marginTop: '0.75em',
          minHeight: 0,
          overflow: 'visible'
        }}>
        <Stack spacing={2} sx={{ marginTop: 0, minHeight: 0, display: 'flex' }}>
          {isAnyPrinterDisabled && <DisabledPrintAlert />}
          <LoadingSpinner isLoading={isLoading || !isInitialPostgresStatusLoaded}>
            {!manifest ? (
              <SearchPromptCard
                title="Manifest No Longer Available"
                description="This manifest is no longer available on the trip sheet. It may have been completed or moved by another user."
                searchPrompt="Use the back arrow to return to the manifest list and select an active manifest."
              />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : `${leftRailWidth} minmax(0, 1fr)`,
                  gap: showCompactHeaderToolbar ? 0.75 : 1,
                  alignItems: 'start',
                  height: 'auto',
                  minHeight: 0
                }}>
                {!isMobile && !showCompactHeaderToolbar ? (
                  <Stack
                    spacing={1}
                    sx={{
                      p: 0.45,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '10px',
                      backgroundColor: theme.palette.background.paper,
                      position: 'sticky',
                      top: 0,
                      width: '100%'
                    }}>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      onClick={toggleAllVisibleSsccs}
                      disabled={isManifestInteractionLocked || !hasVisibleSsccs}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        px: 0.8,
                        py: 0.35,
                        minWidth: 0,
                        width: '100%'
                      }}>
                      {areAllVisibleSsccsOpen ? 'Close All SSCC' : 'Expand All SSCC'}
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      onClick={toggleAllVisibleAsnGroups}
                      disabled={isManifestInteractionLocked || !hasVisibleAsnGroups}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        px: 0.8,
                        py: 0.35,
                        minWidth: 0,
                        width: '100%'
                      }}>
                      {areAllVisibleAsnGroupsOpen ? 'Close All ASN' : 'Expand All ASN'}
                    </StyledButton>
                  </Stack>
                ) : useStackedHeaderLayout ? (
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      onClick={toggleAllVisibleSsccs}
                      disabled={isManifestInteractionLocked || !hasVisibleSsccs}
                      sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
                      {areAllVisibleSsccsOpen ? 'Close All SSCC' : 'Expand All SSCC'}
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      onClick={toggleAllVisibleAsnGroups}
                      disabled={isManifestInteractionLocked || !hasVisibleAsnGroups}
                      sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
                      {areAllVisibleAsnGroupsOpen ? 'Close All ASN' : 'Expand All ASN'}
                    </StyledButton>
                  </Stack>
                ) : (
                  <Box />
                )}
                <Box sx={{ minWidth: 0, minHeight: 0 }}>
                  <TableContainer
                    sx={{
                      maxHeight: tableViewportHeight,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      width: '100%',
                      marginRight: 0,
                      paddingRight: isMobile ? '0.5em' : '0.9em',
                      boxSizing: 'border-box'
                    }}>
                    <Table
                      stickyHeader
                      sx={{
                        tableLayout: 'fixed',
                        width: '100%',
                        '& .MuiTableCell-stickyHeader': {
                          backgroundColor: theme.palette.background.paper,
                          zIndex: 2,
                          paddingTop: '0.6em',
                          paddingBottom: '0.6em',
                          borderBottom: `2px solid ${theme.palette.grey[400]}`
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          textTransform: 'none',
                          letterSpacing: '0.01em'
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                          verticalAlign: 'middle'
                        }
                      }}>
                      <colgroup>
                        <col style={{ width: 90 }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: 104 }} />
                        <col style={{ width: 64 }} />
                      </colgroup>
                      <StyledTableHead>
                        <TableCell sx={partialSelectColumnSx}>
                          <Checkbox
                            size="small"
                            checked={isSelectAllVisibleChecked}
                            indeterminate={isSelectAllVisibleIndeterminate}
                            onChange={(event) => toggleSelectAllVisiblePartialLines(event.target.checked)}
                            disabled={
                              isManifestInteractionLocked ||
                              allLinesReadyForSubmit ||
                              visibleEligibleCount === 0 ||
                              legStatusLock ||
                              !manifestIsOpen ||
                              user.isReadOnly
                            }
                            sx={tableCheckboxSx}
                          />
                        </TableCell>
                        <TableCell width="17%">SSCC #</TableCell>
                        <TableCell width="13%">PO</TableCell>
                        <TableCell width="13%">ASN</TableCell>
                        <TableCell width="10%">Type</TableCell>
                        <TableCell width="13%">Package</TableCell>
                        <TableCell width="13%">Package Qty</TableCell>
                        <TableCell width="15%">Item Status</TableCell>
                        <TableCell sx={actionColumnSx}>Action</TableCell>
                        <TableCell sx={historyColumnSx}>History</TableCell>
                      </StyledTableHead>
                      <TableBody>
                        {filteredSsccs?.map((sscc) => {
                          const isSsccExpanded = Boolean(expandedSsccs[sscc.ssccId]);
                          const previewPackage = sscc.packages[0];
                          const hasMultiplePackages = sscc.packages.length > 1;
                          const showPreviewOnGroupRow = Boolean(previewPackage);
                          const previewAsnGroupExpandable = Boolean(
                            previewPackage && isAsnGroupExpandable(previewPackage)
                          );
                          const previewAsnGroupKey = previewPackage
                            ? getAsnGroupKey(sscc.ssccId, previewPackage.asn)
                            : '';
                          const isPreviewAsnGroupExpanded = Boolean(
                            previewAsnGroupKey && expandedAsnGroups[previewAsnGroupKey]
                          );
                          const previewAsnInventoryLines = previewPackage
                            ? getAsnInventoryLines(sscc, previewPackage.asn)
                            : [];
                          const distinctAsnValuesInSscc = Array.from(
                            new Set(sscc.packages.map((pkg) => (pkg.asn || '').toUpperCase()))
                          );
                          const hasSingleAsnInSscc = distinctAsnValuesInSscc.length === 1;
                          const distinctAsnCountExcludingVarious = getDistinctAsnCountExcludingVarious(sscc);
                          const showPreviewAsnLinesRow =
                            previewAsnGroupExpandable &&
                            isPreviewAsnGroupExpanded &&
                            previewAsnInventoryLines.length > 0;
                          const previewIsStockPurchaseType = Boolean(
                            previewPackage && isStockPurchaseType(previewPackage)
                          );
                          const previewHasDims =
                            previewPackage && previewPackage.width && previewPackage.height && previewPackage.depth;
                          const previewHasFtlAsn = Boolean(previewPackage?.asn?.startsWith(ftlPrefix));
                          const previewIsVarious = Boolean(previewPackage?.asn?.toUpperCase() === variousAsn);
                          const previewIsTransportBookingPackage = Boolean(
                            previewPackage?.asn?.startsWith(transportBookingFormPrefix)
                          );
                          const previewSsccContainsTbf = !sscc.packages.find((pkg) =>
                            pkg.asn.startsWith(transportBookingFormPrefix)
                          );
                          const previewPackageStatusLock = Boolean(
                            previewPackage &&
                            [
                              ManifestPackageStatus.received.toString(),
                              ManifestPackageStatus.qm.toString(),
                              ManifestPackageStatus.direct.toString(),
                              ManifestPackageStatus.qm_pending.toString(),
                              ManifestPackageStatus.direct_pending.toString()
                            ].includes(previewPackage.receivedStatus)
                          );
                          const previewQuantityStatusLock = Boolean(
                            previewPackage &&
                            [
                              ManifestPackageStatus.qm.toString(),
                              ManifestPackageStatus.direct.toString(),
                              ManifestPackageStatus.qm_pending.toString(),
                              ManifestPackageStatus.direct_pending.toString()
                            ].includes(previewPackage.receivedStatus)
                          );
                          const ssccOpenQmMatches = getOpenQmMatchesForSscc(sscc);
                          const ssccOpenQmMatch = ssccOpenQmMatches[0] || null;
                          const ssccQmStatusLock = Boolean(
                            ssccOpenQmMatch ||
                            sscc.packages.some((pkg) =>
                              [
                                ManifestPackageStatus.qm.toString(),
                                ManifestPackageStatus.qm_pending.toString()
                              ].includes(pkg.receivedStatus)
                            )
                          );
                          const previewRowKey = previewPackage ? getPartialRowKey(previewPackage) : '';
                          const previewPartialHistory = previewPackage
                            ? getPartialHistoryForPackage(previewPackage, sscc.ssccId)
                            : [];
                          const previewHistoryContext = previewPackage
                            ? getHistoryDialogContext(sscc.ssccId, previewPackage, 'Package 1')
                            : null;
                          const isPreviewCompletedPartialLine = previewPackage
                            ? isPartialSuccess(previewPackage)
                            : false;
                          const isSameAsnGroupInSscc = hasMultiplePackages && hasSingleAsnInSscc && !previewIsVarious;
                          const isSameAsnGroupFullyMatched = isSameAsnGroupInSscc
                            ? sscc.packages.every((pkg) => Number(pkg.quantity || 0) === Number(pkg.maxQuantity || 0))
                            : true;
                          const canSelectPreviewPartial = Boolean(
                            previewPackage &&
                            isPackageEligibleForPartialSubmit(previewPackage) &&
                            !isPreviewCompletedPartialLine &&
                            isSameAsnGroupFullyMatched
                          );
                          const canShowPreviewTickBox =
                            (!hasMultiplePackages || (hasSingleAsnInSscc && !previewIsVarious)) &&
                            canSelectPreviewPartial &&
                            !legStatusLock &&
                            manifestIsOpen &&
                            !user.isReadOnly;
                          const isPreviewSelectedPartial = Boolean(
                            previewPackage && (allLinesReadyForSubmit || selectedPartialLineKeys[previewRowKey])
                          );
                          const canShowPreviewDirectReceipt = Boolean(
                            previewPackage &&
                            showPreviewOnGroupRow &&
                            (shouldShowButtonsForTBF(previewPackage, sscc) ||
                              shouldShowButtonsForNonTBF(previewPackage, true, false, previewSsccContainsTbf))
                          );
                          const shouldUsePreviewAsnQm = Boolean(
                            previewPackage && shouldShowButtonsForTBF(previewPackage, sscc)
                          );
                          const previewQmScope = shouldUsePreviewAsnQm ? 'asn' : 'sscc';
                          const previewQmOpenKey = previewPackage
                            ? getQmOpenKey(previewQmScope, sscc.ssccId, previewPackage.asn || '')
                            : '';
                          const isPreviewQmOpening = Boolean(previewQmOpenKey && pendingQmOpenKey === previewQmOpenKey);
                          const multiAsnCheckedCount = hasMultiplePackages
                            ? Array.from(
                                new Set(
                                  sscc.packages
                                    .filter((pkg) => isPackageEligibleForPartialSubmit(pkg))
                                    .map((pkg) => getPartialRowKey(pkg))
                                )
                              ).filter((rowKey) => selectedPartialLineKeys[rowKey]).length
                            : 0;
                          return (
                            <Fragment key={`${sscc.ssccId}-group-fragment`}>
                              <StyledTableRow
                                key={`${sscc.ssccId}-group`}
                                sx={{
                                  ...(isSsccExpanded && hasMultiplePackages
                                    ? {
                                        '& td': {
                                          borderBottomWidth: 0
                                        }
                                      }
                                    : {})
                                }}>
                                <TableCell sx={partialSelectColumnSx}>
                                  {ssccOpenQmMatch ? (
                                    renderQmCheckboxAreaChip(ssccOpenQmMatch, {
                                      aggregateCount: ssccOpenQmMatches.length,
                                      aggregateMatches: ssccOpenQmMatches
                                    })
                                  ) : previewPackage &&
                                    (!hasMultiplePackages || (hasSingleAsnInSscc && !previewIsVarious)) ? (
                                    isPreviewCompletedPartialLine ? (
                                      <CheckCircleIcon fontSize="small" sx={partialSuccessIconSx} />
                                    ) : canShowPreviewTickBox ? (
                                      <Checkbox
                                        size="small"
                                        checked={isPreviewSelectedPartial}
                                        onChange={(event) =>
                                          togglePartialLineSelection(previewPackage, event.target.checked)
                                        }
                                        disabled={isManifestInteractionLocked || allLinesReadyForSubmit}
                                        sx={tableCheckboxSx}
                                      />
                                    ) : null
                                  ) : !allLinesReadyForSubmit && multiAsnCheckedCount > 0 ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap'
                                      }}>
                                      {multiAsnCheckedCount} checked
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', pl: 0, pr: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: '2.25em', display: 'flex', justifyContent: 'center' }}>
                                      {hasMultiplePackages ? (
                                        <IconButton
                                          size="small"
                                          disabled={isManifestInteractionLocked}
                                          onClick={() => toggleSsccExpanded(sscc.ssccId)}>
                                          {isSsccExpanded ? (
                                            <KeyboardArrowDownIcon sx={{ color: theme.palette.info.main }} />
                                          ) : (
                                            <KeyboardArrowRightIcon />
                                          )}
                                        </IconButton>
                                      ) : null}
                                    </Box>
                                    <Typography sx={{ fontWeight: 'bold' }}>{sscc.ssccId}</Typography>
                                    {!ssccOpenQmMatch && ssccQmStatusLock ? (
                                      <Chip size="small" label="QM" color="warning" variant="outlined" />
                                    ) : null}
                                  </Stack>
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow ? previewPackage?.poNumber : ''}
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow ? (
                                    <Stack spacing={0.25}>
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        {previewAsnGroupExpandable ? (
                                          <Box
                                            sx={{
                                              width: asnIconSlotWidth,
                                              height: asnIconSlotWidth,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}>
                                            <IconButton
                                              size="small"
                                              disabled={isManifestInteractionLocked}
                                              onClick={() => toggleAsnGroupExpanded(sscc.ssccId, previewPackage!.asn)}
                                              sx={{ padding: 0 }}>
                                              {isPreviewAsnGroupExpanded ? (
                                                <KeyboardArrowDownIcon
                                                  fontSize="small"
                                                  sx={{ color: theme.palette.info.main }}
                                                />
                                              ) : (
                                                <KeyboardArrowRightIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Box sx={{ width: asnIconSlotWidth, height: asnIconSlotWidth }} />
                                        )}
                                        <Typography
                                          sx={
                                            doesAsnMatchSearch(previewPackage?.asn)
                                              ? { color: theme.palette.info.main, fontWeight: 700, lineHeight: 1.2 }
                                              : { lineHeight: 1.2 }
                                          }>
                                          {renderHighlightedAsn(previewPackage?.asn)}
                                        </Typography>
                                      </Stack>
                                      {hasMultiplePackages ? (
                                        <Typography
                                          variant="caption"
                                          color={theme.palette.text.secondary}
                                          sx={{ pl: asnSecondaryTextIndent }}>
                                          {formatAsnCountLabel(distinctAsnCountExcludingVarious)}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  ) : (
                                    ''
                                  )}
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow && previewIsStockPurchaseType ? (
                                    <Chip size="small" label="Stock" variant="outlined" />
                                  ) : (
                                    ''
                                  )}
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow ? (
                                    <Stack>
                                      <Typography>{previewPackage?.packageType}</Typography>
                                      {previewHasDims ? (
                                        <Typography variant="body2" color={theme.palette.text.secondary}>
                                          {previewPackage?.height} x {previewPackage?.width} x {previewPackage?.depth}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color={theme.palette.text.secondary}>
                                      {sscc.packages.length} packages
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow && previewPackage ? (
                                    previewHasFtlAsn &&
                                    !previewIsTransportBookingPackage &&
                                    !previewIsVarious &&
                                    (!hasMultiplePackages || (hasSingleAsnInSscc && !previewIsVarious)) ? (
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <OutlinedInput
                                          sx={quantityInputSx}
                                          inputProps={{
                                            style: {
                                              padding: '0.45em'
                                            },
                                            min: 0,
                                            max: previewPackage.maxQuantity,
                                            step: 1
                                          }}
                                          type="number"
                                          value={previewPackage.quantity}
                                          onChange={(event) => {
                                            onQuantityChange(previewPackage.id, event.target.value);
                                          }}
                                          disabled={
                                            isManifestInteractionLocked ||
                                            isPreviewCompletedPartialLine ||
                                            previewQuantityStatusLock ||
                                            ssccQmStatusLock ||
                                            legStatusLock ||
                                            !manifestIsOpen ||
                                            user.isReadOnly
                                          }
                                        />
                                        <Typography variant="body2">of {previewPackage.maxQuantity}</Typography>
                                      </Stack>
                                    ) : canShowPreviewDirectReceipt ? (
                                      renderDirectReceiptButton(
                                        previewPackage,
                                        isPreviewCompletedPartialLine ||
                                          previewPackageStatusLock ||
                                          ssccQmStatusLock ||
                                          legStatusLock ||
                                          !manifestIsOpen ||
                                          user.isReadOnly
                                      )
                                    ) : (
                                      ''
                                    )
                                  ) : (
                                    ''
                                  )}
                                </TableCell>
                                <TableCell sx={{ pl: 0, pr: 0 }}>
                                  {showPreviewOnGroupRow
                                    ? getPackageItemStatusLabel(previewPackage, ssccOpenQmMatch)
                                    : ''}
                                </TableCell>
                                <TableCell sx={actionColumnSx}>
                                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                    <LightInfoButton
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        const previewAsn = previewPackage?.asn || '';
                                        if (!previewAsn) {
                                          return;
                                        }

                                        if (shouldUsePreviewAsnQm) {
                                          void openQmForAsn(previewAsn, sscc.ssccId);
                                          return;
                                        }

                                        if (hasSingleAsnInSscc) {
                                          openQmScopePicker(previewAsn, sscc.ssccId);
                                          return;
                                        }

                                        void openQmForSscc(previewAsn, sscc.ssccId);
                                      }}
                                      disabled={
                                        isManifestInteractionLocked ||
                                        !previewPackage ||
                                        Boolean(pendingQmOpenKey) ||
                                        isPreviewCompletedPartialLine ||
                                        previewPackageStatusLock ||
                                        ssccQmStatusLock ||
                                        isAnyPrinterDisabled ||
                                        legStatusLock ||
                                        !isQmSiteInboundEnabled ||
                                        !manifestIsOpen
                                      }
                                      sx={tableActionButtonSx}>
                                      {isPreviewQmOpening ? (
                                        <CircularProgress size={14} />
                                      ) : shouldUsePreviewAsnQm ? (
                                        'QM ASN'
                                      ) : hasSingleAsnInSscc ? (
                                        'QM'
                                      ) : (
                                        'QM SSCC'
                                      )}
                                    </LightInfoButton>
                                  </Stack>
                                </TableCell>
                                <TableCell sx={historyColumnSx}>
                                  {previewHistoryContext
                                    ? renderHistoryButton(previewRowKey, previewPartialHistory, previewHistoryContext)
                                    : null}
                                </TableCell>
                              </StyledTableRow>
                              {showPreviewAsnLinesRow ? (
                                <StyledTableRow key={`${sscc.ssccId}-preview-asn-lines`}>
                                  <TableCell colSpan={10}>
                                    {renderAsnLinesDrawer(previewAsnInventoryLines, previewPackage?.asn, sscc.ssccId)}
                                  </TableCell>
                                </StyledTableRow>
                              ) : null}
                              {isSsccExpanded && hasMultiplePackages
                                ? sscc.packages.slice(1).map((pkg) => {
                                    const packageIndex = sscc.packages.findIndex(
                                      (currentPkg) => currentPkg.id === pkg.id
                                    );
                                    const hasFtlAsn = pkg.asn.startsWith(ftlPrefix);
                                    const isVarious = pkg.asn?.toUpperCase() === variousAsn;
                                    const isFirstPackage = pkg === sscc.packages[0];
                                    const isLastPackage = pkg === sscc.packages[sscc.packages.length - 1];
                                    const isFirstAsnRow =
                                      packageIndex === 0 || pkg.asn !== sscc.packages[packageIndex - 1]?.asn;
                                    const sameAsn = pkg.asn === sscc.packages[packageIndex - 1]?.asn;
                                    const nextSameAsn = pkg.asn === sscc.packages[packageIndex + 1]?.asn;
                                    const isAsnGroupLastRow = !nextSameAsn;
                                    const asnGroupExpandable = isAsnGroupExpandable(pkg);
                                    const asnGroupKey = getAsnGroupKey(sscc.ssccId, pkg.asn);
                                    const isAsnGroupExpanded = Boolean(expandedAsnGroups[asnGroupKey]);
                                    const asnInventoryLines = getAsnInventoryLines(sscc, pkg.asn);
                                    const isPreviewAsnGroup = Boolean(previewPackage && pkg.asn === previewPackage.asn);
                                    const showAsnLinesRow =
                                      isAsnGroupLastRow &&
                                      !isPreviewAsnGroup &&
                                      asnGroupExpandable &&
                                      isAsnGroupExpanded &&
                                      asnInventoryLines.length > 0;
                                    const asnSequenceNumber = sscc.packages
                                      .slice(0, packageIndex + 1)
                                      .filter((currentPkg) => currentPkg.asn === pkg.asn).length;
                                    const hasDims = pkg.width && pkg.height && pkg.depth;
                                    const isTransportBookingPackage = pkg.asn.startsWith(transportBookingFormPrefix);
                                    const ssccContainsTbf = !sscc.packages.find((pkg) =>
                                      pkg.asn.startsWith(transportBookingFormPrefix)
                                    );
                                    const packageStatusLock = [
                                      ManifestPackageStatus.received.toString(),
                                      ManifestPackageStatus.qm.toString(),
                                      ManifestPackageStatus.direct.toString(),
                                      ManifestPackageStatus.qm_pending.toString(),
                                      ManifestPackageStatus.direct_pending.toString()
                                    ].includes(pkg.receivedStatus);
                                    const quantityStatusLock = [
                                      ManifestPackageStatus.qm.toString(),
                                      ManifestPackageStatus.direct.toString(),
                                      ManifestPackageStatus.qm_pending.toString(),
                                      ManifestPackageStatus.direct_pending.toString()
                                    ].includes(pkg.receivedStatus);
                                    const rowKey = getPartialRowKey(pkg);
                                    const partialHistory = getPartialHistoryForPackage(pkg, sscc.ssccId);
                                    const historyContext = getHistoryDialogContext(
                                      sscc.ssccId,
                                      pkg,
                                      `Package ${asnSequenceNumber}`
                                    );
                                    const latestPartialStatus = getLatestPartialStatus(pkg, sscc.ssccId);
                                    const openQmMatches = getOpenQmMatchesForPackage(pkg, sscc.ssccId);
                                    const openAnyQmMatch = openQmMatches[0] || null;
                                    const openAttentionQmMatch = getOpenReceiptAttentionQmMatch(pkg, sscc.ssccId);
                                    const hasUnblockedLineForPartial = hasReceivableUnblockedLine(pkg, sscc.ssccId);
                                    const isFailedPartialLine = isFailedPartialStatus(latestPartialStatus);
                                    const isCompletedPartialLine = isPartialSuccess(pkg);
                                    const canSelectPartial =
                                      isPackageEligibleForPartialSubmit(pkg) &&
                                      !isCompletedPartialLine &&
                                      hasUnblockedLineForPartial;
                                    const canShowTickBox =
                                      canSelectPartial && !legStatusLock && manifestIsOpen && !user.isReadOnly;
                                    const isSelectedPartial = Boolean(
                                      allLinesReadyForSubmit || selectedPartialLineKeys[rowKey]
                                    );
                                    const asnQmOpenKey = getQmOpenKey('asn', sscc.ssccId, pkg.asn || '');
                                    const isAsnQmOpening = Boolean(pendingQmOpenKey === asnQmOpenKey);
                                    return (
                                      <Fragment key={pkg.id}>
                                        <StyledTableRow
                                          sx={{
                                            ...(isLastPackage
                                              ? {}
                                              : {
                                                  '& td': {
                                                    borderBottomWidth: 0
                                                  }
                                                }),
                                            ...(isCompletedPartialLine
                                              ? {
                                                  '& td': {
                                                    backgroundColor: theme.palette.action.hover,
                                                    color: theme.palette.text.secondary
                                                  }
                                                }
                                              : {})
                                          }}>
                                          <TableCell sx={partialSelectColumnSx}>
                                            {openAnyQmMatch && !canShowTickBox ? (
                                              renderQmCheckboxAreaChip(openAnyQmMatch, {
                                                aggregateCount: openQmMatches.length,
                                                aggregateMatches: openQmMatches
                                              })
                                            ) : isCompletedPartialLine ? (
                                              <CheckCircleIcon fontSize="small" sx={partialSuccessIconSx} />
                                            ) : canShowTickBox ? (
                                              <Checkbox
                                                size="small"
                                                checked={isSelectedPartial}
                                                onChange={(event) =>
                                                  togglePartialLineSelection(pkg, event.target.checked)
                                                }
                                                disabled={isManifestInteractionLocked || allLinesReadyForSubmit}
                                                sx={tableCheckboxSx}
                                              />
                                            ) : null}
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                                          {<TableCell sx={{ pl: 0, pr: 0 }}>{!sameAsn ? pkg.poNumber : ''}</TableCell>}

                                          <TableCell sx={{ pl: 0, pr: 0 }}>
                                            <Stack spacing={0.25}>
                                              <Stack direction="row" spacing={0.5} alignItems="center">
                                                {isFirstAsnRow && asnGroupExpandable ? (
                                                  <Box
                                                    sx={{
                                                      width: asnIconSlotWidth,
                                                      height: asnIconSlotWidth,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center'
                                                    }}>
                                                    <IconButton
                                                      size="small"
                                                      disabled={isManifestInteractionLocked}
                                                      onClick={() => toggleAsnGroupExpanded(sscc.ssccId, pkg.asn)}
                                                      sx={{ padding: 0 }}>
                                                      {isAsnGroupExpanded ? (
                                                        <KeyboardArrowDownIcon
                                                          fontSize="small"
                                                          sx={{ color: theme.palette.info.main }}
                                                        />
                                                      ) : (
                                                        <KeyboardArrowRightIcon fontSize="small" />
                                                      )}
                                                    </IconButton>
                                                  </Box>
                                                ) : null}
                                                {!sameAsn ? (
                                                  <>
                                                    <Typography
                                                      sx={
                                                        doesAsnMatchSearch(pkg.asn)
                                                          ? {
                                                              color: theme.palette.info.main,
                                                              fontWeight: 700,
                                                              lineHeight: 1.2
                                                            }
                                                          : { lineHeight: 1.2 }
                                                      }>
                                                      {renderHighlightedAsn(pkg.asn)}
                                                    </Typography>
                                                  </>
                                                ) : null}
                                              </Stack>
                                              {sameAsn ? (
                                                <Typography
                                                  variant="caption"
                                                  color={theme.palette.text.secondary}
                                                  sx={{ pl: asnSecondaryTextIndent }}>
                                                  Package {asnSequenceNumber}
                                                </Typography>
                                              ) : null}
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ pl: 0, pr: 0 }}>
                                            {isStockPurchaseType(pkg) ? (
                                              <Chip size="small" label="Stock" variant="outlined" />
                                            ) : (
                                              ''
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ pl: 0, pr: 0 }}>
                                            <Stack>
                                              <Typography>{pkg.packageType}</Typography>
                                              {hasDims && (
                                                <Typography variant="body2" color={theme.palette.text.secondary}>
                                                  {pkg.height} x {pkg.width} x {pkg.depth}
                                                </Typography>
                                              )}
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ pl: 0, pr: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                              {hasFtlAsn && !isTransportBookingPackage ? (
                                                !isVarious && (
                                                  <>
                                                    <OutlinedInput
                                                      sx={quantityInputSx}
                                                      inputProps={{
                                                        style: {
                                                          padding: '0.45em'
                                                        },
                                                        min: 0,
                                                        max: pkg.maxQuantity,
                                                        step: 1
                                                      }}
                                                      type="number"
                                                      value={pkg.quantity}
                                                      onChange={(event) => {
                                                        onQuantityChange(pkg.id, event.target.value);
                                                      }}
                                                      disabled={
                                                        isManifestInteractionLocked ||
                                                        isCompletedPartialLine ||
                                                        quantityStatusLock ||
                                                        Boolean(openAnyQmMatch) ||
                                                        legStatusLock ||
                                                        !manifestIsOpen ||
                                                        user.isReadOnly
                                                      }
                                                    />
                                                    <Typography variant="body2">of {pkg.maxQuantity}</Typography>
                                                  </>
                                                )
                                              ) : (
                                                <>
                                                  {(shouldShowButtonsForTBF(pkg, sscc) ||
                                                    shouldShowButtonsForNonTBF(
                                                      pkg,
                                                      isFirstPackage,
                                                      sameAsn,
                                                      ssccContainsTbf
                                                    )) && (
                                                    <Box>
                                                      {isOnline ? (
                                                        <Tooltip
                                                          title="ASN was not found. Please manually search for the ASN and select the items."
                                                          enterTouchDelay={0}>
                                                          <LightInfoButton
                                                            variant="contained"
                                                            onClick={() => {
                                                              setSelectedASN(pkg.asn);
                                                              setopenDirectModel(true);
                                                            }}
                                                            disabled={
                                                              isManifestInteractionLocked ||
                                                              packageStatusLock ||
                                                              Boolean(openAnyQmMatch) ||
                                                              legStatusLock ||
                                                              !manifestIsOpen
                                                            }
                                                            sx={tableActionButtonSx}>
                                                            Receipt
                                                          </LightInfoButton>
                                                        </Tooltip>
                                                      ) : (
                                                        <Tooltip
                                                          enterTouchDelay={0}
                                                          title="You are offline. This feature is not available when offline.">
                                                          <LightInfoButton
                                                            variant="outlined"
                                                            disabled={isManifestInteractionLocked}
                                                            sx={tableActionButtonSx}>
                                                            Receipt
                                                          </LightInfoButton>
                                                        </Tooltip>
                                                      )}
                                                    </Box>
                                                  )}
                                                </>
                                              )}
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ pl: 0, pr: 0 }}>
                                            <Stack spacing={0.35} alignItems="flex-start">
                                              <Typography variant="body2">
                                                {shouldShowButtonsForTBF(pkg, sscc) ||
                                                shouldShowButtonsForNonTBF(
                                                  pkg,
                                                  isFirstPackage,
                                                  sameAsn,
                                                  ssccContainsTbf
                                                ) ||
                                                ssccContainsTbf
                                                  ? getPackageItemStatusLabel(pkg, openAnyQmMatch)
                                                  : ''}
                                              </Typography>
                                              {isFailedPartialLine ? (
                                                <Chip size="small" label="Last submit failed" color="error" />
                                              ) : null}
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={actionColumnSx}>
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              justifyContent="center">
                                              {shouldShowButtonsForTBF(pkg, sscc) ||
                                              shouldShowButtonsForNonTBF(
                                                pkg,
                                                isFirstPackage,
                                                sameAsn,
                                                ssccContainsTbf
                                              ) ? (
                                                isOnline ? (
                                                  <LightInfoButton
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => void openQmForAsn(pkg.asn, sscc.ssccId)}
                                                    disabled={
                                                      isManifestInteractionLocked ||
                                                      Boolean(pendingQmOpenKey) ||
                                                      isCompletedPartialLine ||
                                                      packageStatusLock ||
                                                      Boolean(openAnyQmMatch && !hasUnblockedLineForPartial) ||
                                                      isAnyPrinterDisabled ||
                                                      legStatusLock ||
                                                      !isQmSiteInboundEnabled ||
                                                      !manifestIsOpen
                                                    }
                                                    sx={tableActionButtonSx}>
                                                    {isAsnQmOpening ? <CircularProgress size={14} /> : 'QM ASN'}
                                                  </LightInfoButton>
                                                ) : (
                                                  <Tooltip
                                                    enterTouchDelay={0}
                                                    title="You are offline. This feature is not available when offline.">
                                                    <LightInfoButton
                                                      variant="outlined"
                                                      size="small"
                                                      disabled={isManifestInteractionLocked}
                                                      sx={tableActionButtonSx}>
                                                      QM ASN
                                                    </LightInfoButton>
                                                  </Tooltip>
                                                )
                                              ) : null}
                                              {openAttentionQmMatch && !openAnyQmMatch ? (
                                                <Tooltip
                                                  title={`QM ${getQmReferenceLabel(openAttentionQmMatch)}`}
                                                  enterTouchDelay={0}>
                                                  <LightInfoButton
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => openQmContext(openAttentionQmMatch)}
                                                    disabled={isManifestInteractionLocked}
                                                    sx={tableActionButtonSx}>
                                                    {getQmReferenceLabel(openAttentionQmMatch)}
                                                  </LightInfoButton>
                                                </Tooltip>
                                              ) : null}
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={historyColumnSx}>
                                            {renderHistoryButton(rowKey, partialHistory, historyContext)}
                                          </TableCell>
                                        </StyledTableRow>
                                        {showAsnLinesRow ? (
                                          <StyledTableRow key={`${pkg.id}-asn-lines`}>
                                            <TableCell colSpan={10}>
                                              {renderAsnLinesDrawer(asnInventoryLines, pkg.asn, sscc.ssccId)}
                                            </TableCell>
                                          </StyledTableRow>
                                        ) : null}
                                      </Fragment>
                                    );
                                  })
                                : null}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </LoadingSpinner>
        </Stack>
        <LoadingModal
          isLoading={isSaving || isSubmitting}
          titleText={isSaving ? 'Saving Data' : isSubmitting ? 'Submitting Manifest' : ''}
          bodyText={
            isSaving
              ? 'Saving current manifest data, please wait.'
              : isSubmitting
                ? 'Submitting Manifest, please wait.'
                : ''
          }
        />
      </PageSection>
      {openRaiseQM && isQmSiteInboundEnabled && (
        <RaiseQMNewForm
          open={openRaiseQM}
          onClose={() => setOpenRaiseQM(false)}
          onSubmit={updateQMStatus}
          type="SITE_INBOUND"
          allowedReasonCodes={selectedQmScope === 'sscc' ? MANIFEST_QM_SSCC_REASON_CODES : MANIFEST_QM_ASN_REASON_CODES}
          initialSsccOrAsn={selectedQmScope === 'sscc' ? selectedQmSsccId : selectedASN}
          siteInboundManifestContext={siteInboundManifestContext}
        />
      )}
      {openDirectModel && (
        <DirectReceiptModel
          open={openDirectModel}
          onClose={() => setopenDirectModel(false)}
          onSubmit={updateDirectStatus}
          asn={selectedASN}
        />
      )}
      <Popover
        open={Boolean(qmPickerAnchorEl)}
        anchorEl={qmPickerAnchorEl}
        onClose={closeQmPicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            p: 1,
            borderRadius: 1,
            minWidth: 140,
            maxWidth: 220
          }
        }}>
        <Stack spacing={0.75}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', px: 0.5 }}>
            Open QM
          </Typography>
          {qmPickerMatches.map((match) => {
            const label = getQmReferenceLabel(match) || String(match.qmId || '');
            return (
              <Chip
                key={`${match.qmId || match.qmNumber}`}
                size="small"
                label={label}
                color="warning"
                variant="outlined"
                onClick={() => {
                  closeQmPicker();
                  void openQmContext(match);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  cursor: 'pointer',
                  '& .MuiChip-label': {
                    px: 0.8,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            );
          })}
        </Stack>
      </Popover>
      <Dialog
        open={Boolean(expandedHistoryRowKey)}
        onClose={closeHistoryDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { maxWidth: '1440px' } }}>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ASN History
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Stack spacing={1.5}>
            {expandedHistoryContext ? (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  backgroundColor: theme.palette.action.hover
                }}>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`SSCC ${expandedHistoryContext.ssccId || '-'}`} />
                  <Chip
                    size="small"
                    label={`ASN ${expandedHistoryContext.asn || '-'}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip size="small" label={`PO ${expandedHistoryContext.poNumber || '-'}`} />
                  <Chip size="small" label={expandedHistoryContext.packageLabel || 'Package'} />
                  <Chip size="small" label={`Type ${expandedHistoryContext.packageType || '-'}`} />
                  <Chip size="small" label={expandedHistoryContext.stockStatus || 'Stock unknown'} variant="outlined" />
                  <Chip size="small" label={`Status ${expandedHistoryContext.status || '-'}`} variant="outlined" />
                </Stack>
              </Box>
            ) : null}
            <TableContainer sx={{ maxHeight: '56vh', overflowX: 'hidden' }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    py: 0.85,
                    px: 0.75,
                    fontSize: '0.82rem',
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 700,
                    lineHeight: 1.2,
                    backgroundColor: theme.palette.grey[100]
                  }
                }}>
                <StyledTableHead>
                  <TableCell sx={{ width: 132 }}>Time</TableCell>
                  <TableCell sx={{ width: 92 }}>User</TableCell>
                  <TableCell sx={{ width: 136 }}>Status</TableCell>
                  <TableCell sx={{ width: 98 }}>Level</TableCell>
                  <TableCell sx={{ width: 104 }}>Source Part ID</TableCell>
                  <TableCell sx={{ width: 154 }}>Line Desc</TableCell>
                  <TableCell align="right" sx={{ width: 58 }}>
                    Pkg Qty
                  </TableCell>
                  <TableCell align="right" sx={{ width: 64 }}>
                    Request
                  </TableCell>
                  <TableCell align="right" sx={{ width: 64 }}>
                    Process
                  </TableCell>
                  <TableCell align="right" sx={{ width: 62 }}>
                    Receipt
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>QM</TableCell>
                </StyledTableHead>
                <TableBody>
                  {expandedHistory.map((entry) => (
                    <StyledTableRow key={entry.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {entry.processedAt ? dayjs(entry.processedAt).format('DD MMM YYYY HH:mm:ss') : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={entry.triggeredBy || 'Unknown'} enterTouchDelay={0}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 112 }}>
                            {formatHistoryUser(entry.triggeredBy)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={getHistoryStatusLabel(entry)}
                          color={String(entry.status || '').toUpperCase() === 'FAILED' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          label={getHistoryScopeLabel(entry)}
                          variant="outlined"
                          color={isSavedSnapshotHistoryLine(entry) ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={isSavedSnapshotHistoryLine(entry) ? '-' : getHistorySourcePartId(entry)}
                          enterTouchDelay={0}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                            {isSavedSnapshotHistoryLine(entry) ? '-' : getHistorySourcePartId(entry)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={isSavedSnapshotHistoryLine(entry) ? '-' : getHistoryLineDescription(entry)}
                          enterTouchDelay={0}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 210 }}>
                            {isSavedSnapshotHistoryLine(entry) ? '-' : getHistoryLineDescription(entry)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{renderHistoryValue(entry.packageQuantity)}</TableCell>
                      <TableCell align="right">
                        {isReceiptResultHistoryLine(entry) ? renderHistoryValue(entry.requestedQuantity) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {isReceiptResultHistoryLine(entry) ? renderHistoryValue(entry.processedQuantity) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {isReceiptResultHistoryLine(entry) ? renderHistoryValue(entry.receiveQuantity) : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={getHistoryErrorDetail(entry) || '-'} enterTouchDelay={0}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 190 }}>
                            {getHistoryErrorDetail(entry) || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {latestExpandedHistory?.status === 'FAILED' ? (
              <Typography variant="body2" color="error">
                Latest submit failed
                {getHistoryErrorDetail(latestExpandedHistory)
                  ? `: ${getHistoryErrorDetail(latestExpandedHistory)}`
                  : '.'}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <StyledButton variant="outlined" onClick={closeHistoryDialog}>
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openQmScopeDialog} onClose={handleCloseQmScopePicker} maxWidth="xs" fullWidth>
        <DialogTitle>Raise QM For</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color={theme.palette.text.secondary}>
            This SSCC has a single ASN. Choose whether to create QM for the full SSCC or only the ASN.
          </Typography>
        </DialogContent>
        <DialogActions>
          <StyledButton
            variant="contained"
            disabled={isManifestInteractionLocked}
            onClick={() => handleSelectQmScope('sscc')}>
            SSCC
          </StyledButton>
          <StyledButton
            variant="contained"
            disabled={isManifestInteractionLocked}
            onClick={() => handleSelectQmScope('asn')}>
            ASN
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={handleCloseQmScopePicker}
            sx={{
              backgroundColor: theme.palette.common.white
            }}>
            Cancel
          </StyledButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={openDirtyDialog}
        onClose={() => setOpenDirtyDialog(false)}
        onConfirm={navigateBackToReceiveManifestList}
        title="Unsaved Changes"
        content="You have unsaved changes. Are you sure you want to leave the page?"
      />
    </>
  );
}
